/**
 * Gewerke- und Paarungskatalog fuer die Ueberschneidungsanalyse.
 *
 * ─── Warum der Katalog im Repo liegt und nicht in der Datenbank ───────────
 * Gleiche Begruendung wie beim Fragenkatalog der Planungsphase
 * (`src/components/planungsphase/fragenkatalog.ts`): ein Projekt haelt fest,
 * mit welcher Katalogfassung seine Ueberschneidungen bewertet wurden. Laege
 * der Katalog in einer Tabelle und wuerde dort fortgeschrieben, aenderte sich
 * rueckwirkend, welche Gefaehrdungen in einem abgeschlossenen Projekt erkannt
 * wurden — und genau das zerstoert den Beweiswert der Akte. Eine neue Fassung
 * erhoeht `KATALOG_VERSION`; Bestandsprojekte behalten ihre Bewertung, weil
 * das Ergebnis beim Erzeugen kopiert und nicht bei jedem Aufruf neu gerechnet
 * wird.
 *
 * ─── Die Codes sind unveraenderlich ──────────────────────────────────────
 * `GK-017` steht im SiGe-Plan und in Besprechungsprotokollen. Ein Code wird
 * nie neu vergeben und nie umsortiert. Entfaellt eine Paarung fachlich, wird
 * sie nicht geloescht, sondern durch eine neue ersetzt und der alte Code
 * bleibt vergeben.
 *
 * ─── Was dieser Katalog NICHT ist ────────────────────────────────────────
 * Er ersetzt die Gefaehrdungsbeurteilung des Unternehmers nicht. Er benennt
 * die KOORDINIERENDE Massnahme des SiGeKo nach § 3 BaustellV — also das, was
 * nur zwischen den Gewerken geregelt werden kann: zeitliche Trennung,
 * raeumliche Trennung, gemeinsam genutzte Sicherungseinrichtung,
 * Abstimmungspflicht.
 *
 * ─── Quellen ─────────────────────────────────────────────────────────────
 * BaustellV (Fassung ab 01.04.2023) §§ 2-4 + Anhang II · RAB 30/31/33 ·
 * ArbSchG §§ 4 und 8 · DGUV Vorschrift 1 §§ 5/6 · DGUV Vorschrift 3 ·
 * DGUV Vorschrift 38 · DGUV Vorschrift 52 · DGUV Information 203-006 ·
 * DGUV Regel 112-194 · BetrSichV · TRBS 2121 Teil 1 · TRBS 3121 ·
 * GefStoffV · TRGS 519/524/528/559 · LaermVibrationsArbSchV ·
 * ASR A1.3/A1.8/A2.1/A2.2/A2.3/A3.6/A5.2 · DIN 4124 · RSA 21 · § 45 StVO.
 */

/**
 * Fassung des Katalogs. Bei jeder inhaltlichen Aenderung erhoehen.
 *
 * Fassung 2: fuenf Gewerke ergaenzt (Tischler, Stuckateur/WDVS,
 * Dachklempner, Sprinkler/Brandschutztechnik, Spezialtiefbau). Dabei sind
 * drei Synonymgruppen gewandert — `sprinkler` weg von SHK, `wdvs` weg von
 * der Fassade, `verbau`/`spundwand` weg vom Erdbau. Die Begruendung steht
 * jeweils an der Fundstelle; keine Paarung wurde entfernt.
 */
export const KATALOG_VERSION = 2;

// ═══════════════════════════════════════════════════════════════════════════
// GEWERKE
// ═══════════════════════════════════════════════════════════════════════════

export type GewerkId =
    | 'rohbau'
    | 'erdbau'
    | 'geruestbau'
    | 'zimmerer_dachdecker'
    | 'kran'
    | 'elektro'
    | 'shk'
    | 'estrich'
    | 'trockenbau'
    | 'maler'
    | 'fliesenleger'
    | 'fassade'
    | 'aufzugsbau'
    | 'abbruch'
    | 'schadstoff'
    | 'strassenbau'
    | 'galabau'
    | 'reinigung'
    | 'tischler'
    | 'stuckateur'
    | 'dachklempner'
    | 'sprinkler'
    | 'spezialtiefbau'
    | 'sonstiges';

/**
 * Rueckgabewert der Zuordnung, wenn der Freitext KEIN Gewerk eindeutig
 * benennt.
 *
 * `unbekannt` ist bewusst etwas anderes als `sonstiges`: `sonstiges` ist eine
 * Aussage des Anwenders ("ein Gewerk, das wir nicht einzeln fuehren"),
 * `unbekannt` ist die Aussage des Programms ("ich weiss es nicht"). Nur der
 * zweite Fall darf die Oberflaeche zu einer Rueckfrage zwingen. Wer beides
 * gleichsetzt, verliert genau die Faelle, die ein Mensch ansehen muss.
 */
export const UNBEKANNT = 'unbekannt';
export type GewerkTreffer = GewerkId | typeof UNBEKANNT;

export interface GewerkEintrag {
    id: GewerkId;
    /** Anzeigetext. Darf geaendert werden, die id nicht. */
    bezeichnung: string;
    /**
     * Schreibvarianten in NORMALISIERTER Form (siehe `normalisiereText`):
     * klein, ohne Umlaute, ohne Trenn- und Leerzeichen. Dadurch decken
     * wenige Eintraege alle Bindestrich-, Mehrzahl- und Grossschreibungs-
     * varianten ab; nur echte Wortunterschiede und Tippfehler brauchen eine
     * eigene Zeile.
     */
    synonyme: string[];
}

/**
 * Ab dieser Laenge darf ein Begriff auch als Teilstring greifen. Kuerzere
 * Begriffe muessen das gesamte Feld ausfuellen.
 *
 * Der Grund steht in einem einzigen Wort: "Krankenhaus". Wuerde `kran` als
 * Teilstring gelten, waere jeder Umbau eines Krankenhauses ein Kranbetrieb.
 * Dieselbe Falle stellen `dach` (Dachklempner, Flachdachsanierung), `putz`
 * und `shk`. Fuer diese Faelle stehen laengere Varianten in der Synonymliste.
 */
export const MIN_TEILTREFFER_LAENGE = 5;

export const GEWERKE: GewerkEintrag[] = [
    {
        id: 'rohbau',
        bezeichnung: 'Rohbau (Maurer-, Beton- und Stahlbetonarbeiten)',
        synonyme: [
            'rohbau', 'rohbauer', 'maurer', 'maurerbetrieb', 'mauerwerk', 'mauerwerksbau',
            'beton', 'betonbau', 'betonbauer', 'betonieren', 'stahlbeton', 'stahlbau',
            'fertigteilmontage',
            'schalung', 'schalungsbau', 'bewehrung', 'bewehrungsbau', 'eisenflechter',
            'massivbau', 'fertigteil', 'fertigteilbau', 'betonfertigteile',
        ],
    },
    {
        id: 'erdbau',
        bezeichnung: 'Erd- und Tiefbau',
        synonyme: [
            // Verbau, Spundwand und Spezialtiefbau sind mit Fassung 2 in das
            // eigene Gewerk `spezialtiefbau` gewandert: dort arbeiten schwere
            // Bohr- und Rammgeraete mit eigenem Standsicherheits- und
            // Setzungsrisiko, das mit einem Bagger nichts zu tun hat.
            'erdbau', 'erdarbeiten', 'erdaushub', 'tiefbau', 'tiefbauer', 'aushub',
            'bagger', 'baggerarbeiten', 'baggerfahrer', 'baugrube', 'grabenbau',
            'kanalbau', 'kanalisation', 'bodenaustausch', 'wasserhaltung',
        ],
    },
    {
        id: 'geruestbau',
        bezeichnung: 'Gerüstbau',
        synonyme: [
            'geruest', 'geruestbau', 'geruestbauer', 'geruestersteller', 'gerueststeller',
            'fassadengeruest', 'arbeitsgeruest', 'schutzgeruest', 'fanggeruest',
            'dachfanggeruest', 'traggeruest', 'raumgeruest', 'geruestbaufirma',
        ],
    },
    {
        id: 'zimmerer_dachdecker',
        bezeichnung: 'Zimmerer- und Dachdeckerarbeiten',
        synonyme: [
            'zimmerer', 'zimmerei', 'zimmermann', 'zimmererbetrieb', 'holzbau',
            'holzbauer', 'dachstuhl', 'dachdecker', 'dachdeker', 'dachdeckerei',
            'dachabdichtung', 'flachdach', 'steildach', 'dacharbeiten', 'bedachung',
        ],
    },
    {
        id: 'kran',
        bezeichnung: 'Kranbetrieb und Hebezeuge',
        synonyme: [
            'kran', 'kranbetrieb', 'kranarbeiten', 'kranfuehrer', 'krananlage',
            'turmdrehkran', 'autokran', 'mobilkran', 'fahrzeugkran', 'raupenkran',
            'hebezeug', 'hebearbeiten', 'schwerlast', 'schwerlasttransport',
            'anschlaeger', 'betonpumpe',
        ],
    },
    {
        id: 'elektro',
        bezeichnung: 'Elektrotechnik',
        synonyme: [
            'elektro', 'elektrik', 'elektriker', 'elektiker', 'elektrotechnik',
            'elektroinstallation', 'elektroniker', 'elektrofachkraft', 'etechnik',
            'starkstrom', 'schwachstrom', 'niederspannung', 'mittelspannung',
            'blitzschutz', 'photovoltaik', 'pvanlage', 'datentechnik', 'baustrom',
            'beleuchtungsbau',
        ],
    },
    {
        id: 'shk',
        bezeichnung: 'Sanitär, Heizung, Klima und Lüftung',
        synonyme: [
            'shk', 'sanitaer', 'sanitaerinstallation', 'heizung', 'heizungsbau',
            'heizungsbauer', 'klima', 'klimatechnik', 'lueftung', 'lueftungsbau',
            // Sprinkler und Loeschanlagen sind mit Fassung 2 in das eigene
            // Gewerk `sprinkler` gewandert — Begruendung bei GK-079.
            'lueftungstechnik', 'kaeltetechnik', 'kaelteanlagen', 'rohrleitungsbau',
            'rohrleger', 'installateur', 'hlk', 'hlks',
        ],
    },
    {
        id: 'estrich',
        bezeichnung: 'Estricharbeiten',
        synonyme: [
            'estrich', 'estrichleger', 'estrichbau', 'zementestrich', 'anhydritestrich',
            'fliessestrich', 'heizestrich', 'bodenaufbau',
        ],
    },
    {
        id: 'trockenbau',
        bezeichnung: 'Trockenbau',
        synonyme: [
            'trockenbau', 'trockenbauer', 'gipskarton', 'gipsfaser', 'staenderwerk',
            'vorsatzschale', 'abgehaengtedecke', 'systemtrennwand', 'akustikdecke',
        ],
    },
    {
        id: 'maler',
        bezeichnung: 'Maler-, Lackier- und Bodenbelagsarbeiten',
        synonyme: [
            'maler', 'malerbetrieb', 'malerei', 'anstrich', 'anstreicher', 'lackier',
            'lackierer', 'beschichtung', 'beschichtungsarbeiten', 'tapezier',
            'tapezierer', 'bodenbelag', 'bodenleger', 'parkettleger', 'teppichleger',
            'spachtelarbeiten',
        ],
    },
    {
        id: 'fliesenleger',
        bezeichnung: 'Fliesen-, Platten- und Mosaikarbeiten',
        synonyme: [
            'fliesen', 'fliesenleger', 'fliessenleger', 'fliesenarbeiten', 'fliesenbau',
            'plattenleger', 'mosaikleger', 'natursteinverlegung',
        ],
    },
    {
        id: 'fassade',
        bezeichnung: 'Fenster, Fassade und Metallbau',
        synonyme: [
            'fenster', 'fensterbau', 'fensterbauer', 'fassade', 'fassadenbau',
            'vorhangfassade', 'elementfassade', 'metallbau', 'metallbauer', 'schlosser',
            // WDVS ist mit Fassung 2 beim `stuckateur` — es ist eine
            // Putzleistung mit brennbarer Daemmung, keine Elementfassade.
            'glasbau', 'glaser', 'sonnenschutz',
        ],
    },
    {
        id: 'aufzugsbau',
        bezeichnung: 'Aufzugsbau',
        synonyme: [
            'aufzug', 'aufzugsbau', 'aufzugsmontage', 'aufzugstechnik', 'fahrstuhl',
            'personenaufzug', 'lastenaufzug', 'fahrtreppe', 'foerderanlage',
        ],
    },
    {
        id: 'abbruch',
        bezeichnung: 'Abbruch und Rückbau',
        synonyme: [
            'abbruch', 'abbrucharbeiten', 'abbruchunternehmen', 'rueckbau',
            'entkernung', 'demontage', 'abriss', 'abbruchbagger', 'sprengarbeiten',
        ],
    },
    {
        id: 'schadstoff',
        bezeichnung: 'Asbest- und Schadstoffsanierung',
        synonyme: [
            'asbest', 'asbestsanierung', 'schadstoff', 'schadstoffsanierung',
            'gefahrstoffsanierung', 'kmfsanierung', 'mineralwolle', 'pcbsanierung',
            'paksanierung', 'schimmelsanierung', 'trgs519',
            'schwarzbereich',
        ],
    },
    {
        id: 'strassenbau',
        bezeichnung: 'Straßenbau und Verkehrssicherung',
        synonyme: [
            'strasse', 'strassenbau', 'strassenbauer', 'asphalt', 'asphaltarbeiten',
            'fahrbahn', 'fahrbahnsanierung', 'verkehrssicherung', 'verkehrsfuehrung',
            'baustellenabsicherung', 'markierungsarbeiten', 'wegebau',
        ],
    },
    {
        id: 'galabau',
        bezeichnung: 'Garten- und Landschaftsbau',
        synonyme: [
            'gartenbau', 'galabau', 'landschaftsbau', 'landschaftsgaertner',
            'aussenanlagen', 'freianlagen', 'begruenung', 'bepflanzung', 'pflanzarbeiten',
            'baumpflege', 'rasenbau',
        ],
    },
    {
        id: 'reinigung',
        bezeichnung: 'Bau- und Endreinigung',
        synonyme: [
            'reinigung', 'bauendreinigung', 'endreinigung', 'zwischenreinigung',
            'grundreinigung', 'gebaeudereinigung', 'reinigungsfirma', 'putzkolonne',
        ],
    },
    {
        id: 'tischler',
        bezeichnung: 'Tischler- und Schreinerarbeiten',
        synonyme: [
            'tischler', 'tischlerei', 'schreiner', 'schreinerei', 'innentueren',
            'tuerenmontage', 'zargenmontage', 'einbaumoebel', 'moebelbau',
            'holztreppen', 'holzverkleidung',
        ],
    },
    {
        id: 'stuckateur',
        bezeichnung: 'Stuckateur-, Putz- und WDVS-Arbeiten',
        synonyme: [
            'stuck', 'stuckateur', 'stukkateur', 'verputzer', 'putzarbeiten',
            'innenputz', 'aussenputz', 'maschinenputz', 'wdvs',
            'waermedaemmverbundsystem', 'fassadendaemmung', 'daemmputz',
        ],
    },
    {
        id: 'dachklempner',
        bezeichnung: 'Dachklempner und Bauspengler',
        // "Klempner" allein bleibt mehrdeutig (siehe MEHRDEUTIG) — nur die
        // zusammengesetzten Formen sind eindeutig. Die Verdraengungsregel in
        // `gewerkAusText` sorgt dafuer, dass "Dachklempner" hier landet und
        // "Klempner" trotzdem unbekannt bleibt.
        synonyme: [
            'dachklempner', 'dachklempnerei', 'bauklempner', 'dachspengler',
            'bauspengler', 'flaschner', 'dachrinne', 'dachentwaesserung',
            'attikablech', 'kehlblech', 'metalldach', 'titanzink',
            'blitzschutzanschluss',
        ],
    },
    {
        id: 'sprinkler',
        bezeichnung: 'Sprinkler- und Brandschutztechnik',
        synonyme: [
            'sprinkler', 'sprinkleranlage', 'sprinklerbau', 'loeschanlage',
            'feuerloeschanlage', 'brandschutztechnik', 'brandmeldeanlage', 'bma',
            'rauchabzug', 'rwa', 'brandschottung', 'brandschutzabschottung',
        ],
    },
    {
        id: 'spezialtiefbau',
        bezeichnung: 'Spezialtiefbau (Verbau, Bohrpfähle, Baugrubensicherung)',
        synonyme: [
            'spezialtiefbau', 'verbau', 'baugrubenverbau', 'baugrubensicherung',
            'spundwand', 'schlitzwand', 'bohrpfahl', 'bohrpfaehle', 'pfahlgruendung',
            'mikropfahl', 'ankerbohrung', 'rammarbeiten', 'unterfangung', 'grundbau',
        ],
    },
    {
        id: 'sonstiges',
        bezeichnung: 'Sonstiges Gewerk',
        synonyme: ['sonstige', 'sonstigesgewerk', 'uebrige', 'weiteregewerke'],
    },
];

const GEWERK_NACH_ID = new Map<GewerkId, GewerkEintrag>(GEWERKE.map(g => [g.id, g]));

export const gewerkBezeichnung = (id: GewerkTreffer): string =>
    id === UNBEKANNT ? 'nicht zugeordnet' : (GEWERK_NACH_ID.get(id)?.bezeichnung ?? id);

// ═══════════════════════════════════════════════════════════════════════════
// MEHRDEUTIGE BEGRIFFE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Begriffe, die auf der Baustelle ueblich sind, aber KEIN Gewerk bezeichnen.
 * Taucht einer davon auf, zaehlt er wie ein zweites, unbestimmtes Gewerk —
 * die Zuordnung faellt damit auf `unbekannt` zurueck.
 *
 * Das ist kein Schoenheitsfehler, sondern der Zweck: "Klempner" heisst im
 * Norden Sanitaerinstallateur und im Sueden Bauspengler. Wer hier raet,
 * schreibt einem Dachgewerk die Gefaehrdungen der Rohrmontage zu — und der
 * SiGe-Plan sagt dann etwas Falsches statt gar nichts.
 */
export interface MehrdeutigerBegriff {
    /** Normalisierte Form (siehe `normalisiereText`). */
    begriff: string;
    grund: string;
}

export const MEHRDEUTIG: MehrdeutigerBegriff[] = [
    // Bleibt mehrdeutig, auch seit es das Gewerk `dachklempner` gibt: das
    // Wort allein entscheidet die Frage nicht, nur die zusammengesetzte Form
    // ("Dachklempner", "Bauspengler") tut es.
    { begriff: 'klempner', grund: 'Je nach Region Sanitaerinstallateur (SHK) oder Bauklempner am Dach.' },
    { begriff: 'spengler', grund: 'Wie Klempner — Dach/Fassade oder Sanitaer.' },
    { begriff: 'haustechnik', grund: 'Fasst Elektro und SHK zusammen; die Gefaehrdungen unterscheiden sich vollstaendig.' },
    { begriff: 'gebaeudetechnik', grund: 'Wie Haustechnik.' },
    { begriff: 'tga', grund: 'Technische Gebaeudeausruestung = Elektro und SHK gemeinsam.' },
    { begriff: 'hochbau', grund: 'Sammelbegriff fuer die Bauart, nicht fuer das ausfuehrende Gewerk.' },
    { begriff: 'bauaufzug', grund: 'Geraet der Baustelleneinrichtung, nicht das Gewerk Aufzugsbau.' },
    { begriff: 'anlagenbau', grund: 'SHK, Elektro oder Stahlbau — nicht entscheidbar.' },
    { begriff: 'pflaster', grund: 'Strassenbau oder Garten-/Landschaftsbau.' },
    { begriff: 'ausbaugewerke', grund: 'Sammelbegriff ohne Gewerksangabe.' },
    { begriff: 'nachunternehmer', grund: 'Vertragsrolle, kein Gewerk.' },
    { begriff: 'subunternehmer', grund: 'Vertragsrolle, kein Gewerk.' },
    { begriff: 'fremdfirma', grund: 'Vertragsrolle, kein Gewerk.' },
    { begriff: 'diverse', grund: 'Sammelangabe.' },
    { begriff: 'verschiedene', grund: 'Sammelangabe.' },
    { begriff: 'allegewerke', grund: 'Sammelangabe.' },
    { begriff: 'mehreregewerke', grund: 'Sammelangabe.' },
];

// ═══════════════════════════════════════════════════════════════════════════
// NORMALISIERUNG  (rein, deterministisch, ohne Abhaengigkeiten)
// ═══════════════════════════════════════════════════════════════════════════

/** Umlaute werden deutsch aufgeloest, NICHT entakzentuiert: "ü" wird "ue",
 *  nicht "u". Sonst faende "Gerüst" den Eintrag "geruest" nicht mehr. */
const UMLAUTE: Record<string, string> = {
    'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss',
};

/**
 * Bringt einen Freitext auf die Vergleichsform: klein, ohne Umlaute, ohne
 * Akzente, ohne Trenn-, Leer- und Satzzeichen.
 *
 * Die Reihenfolge ist wichtig: erst die deutschen Umlaute aufloesen, danach
 * die restlichen Akzente entfernen. Andersherum wuerde die Zerlegung nach
 * NFD aus "ü" ein "u" machen, bevor die Regel "ue" greifen kann.
 */
export const normalisiereText = (roh: string): string =>
    roh
        .toLowerCase()
        .replace(/[äöüß]/g, z => UMLAUTE[z])
        .normalize('NFD')
        // \p{M} = kombinierende Zeichen. Nach NFD haengt jeder Akzent als
        // eigenes Zeichen hinter seinem Buchstaben und faellt hier weg.
        .replace(/\p{M}/gu, '')
        .replace(/[^a-z0-9]+/g, '');

interface Fund {
    begriff: string;
    /** `null` = mehrdeutiger Begriff (zaehlt als unbestimmtes Gewerk). */
    id: GewerkId | null;
    /**
     * Alle Fundstellen im normalisierten Text (Startindizes).
     *
     * Ohne die Stellen ist ein Fund nur ein Wort, und dann laesst sich nicht
     * mehr unterscheiden, ob "Beton" in "Betonpumpe" steckt oder daneben —
     * siehe `verdraengt`.
     */
    stellen: number[];
}

/** Jede Fundstelle von `begriff` in `norm`. */
const fundstellen = (norm: string, begriff: string): number[] => {
    const raus: number[] = [];
    for (let i = norm.indexOf(begriff); i >= 0; i = norm.indexOf(begriff, i + 1)) raus.push(i);
    return raus;
};

/**
 * Ordnet einen Freitext genau einem Gewerk zu — oder gibt `unbekannt`
 * zurueck.
 *
 * ─── Warum kein unscharfer Abgleich ("fuzzy matching") ────────────────────
 * Eine Levenshtein- oder Trigramm-Naehe wuerde "Estrich" und "Elektro" oder
 * "Maler" und "Maurer" gelegentlich verwechseln. Die Folge waere kein
 * fehlender, sondern ein FALSCHER Eintrag im SiGe-Plan: eine Gefaehrdung, die
 * es nicht gibt, und eine, die fehlt. Ein nicht erkanntes Gewerk faellt in
 * der Oberflaeche sofort auf und wird in zehn Sekunden von Hand zugeordnet;
 * eine falsche Zuordnung faellt niemandem auf, weil sie plausibel aussieht.
 * Deshalb: nur exakte und eindeutige Teiltreffer, im Zweifel `unbekannt`.
 *
 * ─── Die drei Regeln ──────────────────────────────────────────────────────
 * 1. Steht im Feld nichts als ein Gewerksname, gilt dieser. Ein voll
 *    ausgefuelltes Feld ist eindeutig, da braucht es keine weitere Pruefung.
 * 2. Sonst zaehlen alle Begriffe, die als Teilstring vorkommen und
 *    mindestens `MIN_TEILTREFFER_LAENGE` Zeichen haben. Ein laengerer Fund
 *    verdraengt einen kuerzeren nur dort, wo dieser AN DERSELBEN STELLE steht
 *    und damit nichts Eigenes beitraegt — so wird aus "Fassadengerüst" der
 *    Geruestbau, waehrend "Betonarbeiten mit Betonpumpe" zwei Gewerke bleiben
 *    (siehe `verdraengt` unten).
 * 3. Bleibt danach genau ein Gewerk uebrig, gilt es. Bleiben zwei uebrig
 *    ("Rohbau und Sanitär"), ist das kein Zuordnungsproblem, sondern ein
 *    Arbeitsschritt mit zwei Gewerken — auch dann `unbekannt`.
 */
export const gewerkAusText = (text: string | null | undefined): GewerkTreffer => {
    const norm = normalisiereText(text || '');
    if (!norm) return UNBEKANNT;

    for (const g of GEWERKE) {
        if (g.synonyme.includes(norm)) return g.id;
    }

    const funde: Fund[] = [];
    for (const g of GEWERKE) {
        for (const s of g.synonyme) {
            if (s.length >= MIN_TEILTREFFER_LAENGE && norm.includes(s)) {
                funde.push({ begriff: s, id: g.id, stellen: fundstellen(norm, s) });
            }
        }
    }
    for (const m of MEHRDEUTIG) {
        const trifft = m.begriff === norm
            || (m.begriff.length >= MIN_TEILTREFFER_LAENGE && norm.includes(m.begriff));
        if (trifft) funde.push({ begriff: m.begriff, id: null, stellen: fundstellen(norm, m.begriff) });
    }

    // ═══ VERDRAENGT WIRD NUR, WER NICHTS EIGENES BEITRAEGT ═══════════════
    // Ein laengerer Fund schluckt einen kuerzeren genau dann, wenn JEDE
    // Fundstelle des kuerzeren innerhalb einer Fundstelle des laengeren liegt —
    // wenn der kuerzere also gar keine eigene Stelle im Text hat, sondern nur
    // ein Teilstueck desselben Wortes ist.
    //
    // Der Wortvergleich allein (`lang.begriff.includes(kurz.begriff)`) reicht
    // dafuer NICHT, und daran ist das Modul gescheitert: in "Betonarbeiten mit
    // Betonpumpe" steht "Beton" (Rohbau) an Stelle 0 UND in "Betonpumpe" (Kran)
    // an Stelle 16. Die Wortregel verdraengte trotzdem, der Schritt wurde zum
    // Kran, wanderte in dessen Huelle, und die Rohbau-Paarungen (Rohbau ×
    // Gerüst, Rohbau × Elektro) wurden nie vorgeschlagen. Die Stellenregel
    // laesst "Beton@0" stehen — zwei Gewerke im selben Feld, und die richtige
    // Antwort ist `unbekannt`. Das ist die tragende Entscheidung dieses
    // Katalogs: eine falsche Zuordnung faellt niemandem auf, weil sie plausibel
    // aussieht; ein `unbekannt` faellt sofort auf und ist von Hand erledigt.
    //
    // Zusammengesetzte Woerter bleiben davon unberuehrt, denn dort deckt der
    // laengere Fund den kuerzeren wirklich ab — auch ueber Gewerksgrenzen
    // hinweg, wo genau das gemeint ist: "Spezialtiefbau" schluckt "Tiefbau"
    // (Erdbau), "Abbruchbagger" schluckt "Bagger" (Erdbau), "Fassadengerüst"
    // schluckt "Fassade", "Dachklempner" schluckt das mehrdeutige "Klempner".
    const verdraengt = (kurz: Fund, lang: Fund): boolean =>
        lang.begriff.length > kurz.begriff.length
        && kurz.stellen.every(k => lang.stellen.some(
            l => l <= k && k + kurz.begriff.length <= l + lang.begriff.length));

    const bleibt = funde.filter(f => !funde.some(o => verdraengt(f, o)));

    if (bleibt.some(f => f.id === null)) return UNBEKANNT;
    const ids = new Set(bleibt.map(f => f.id as GewerkId));
    return ids.size === 1 ? [...ids][0] : UNBEKANNT;
};

// ═══════════════════════════════════════════════════════════════════════════
// PAARUNGEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Schwere der gegenseitigen Gefaehrdung.
 *
 * `hoch`   — toedlicher oder schwerer bleibender Schaden ist der Regelfall des
 *            Schadensereignisses: Absturz, herabfallende Last, Verschuettung,
 *            Stromdurchgang, Brand oder Explosion, Schadstoffexposition,
 *            angefahren werden.
 * `mittel` — Verletzung mit Arbeitsausfall oder Gesundheitsschaden bei
 *            wiederholter Einwirkung: Sturz auf gleicher Ebene, Schnitt,
 *            Laerm, Staub, Reizung.
 * `gering` — Belaestigung, leichte Verletzung, Ablaufstoerung mit nur
 *            mittelbarer Gefaehrdung.
 *
 * Die Skala ist dreistufig und nicht feiner. Eine fuenf- oder zehnstufige
 * Bewertung suggeriert eine Genauigkeit, die aus einem Katalog nicht kommen
 * kann — die Einstufung entscheidet hier nur darueber, was zwingend in den
 * SiGe-Plan gehoert (`hoch`) und was der Koordinator abwaegen darf.
 */
export type Schwere = 'hoch' | 'mittel' | 'gering';

export const SCHWERE_LABEL: Record<Schwere, string> = {
    hoch: 'Hoch — in den SiGe-Plan aufnehmen',
    mittel: 'Mittel — koordinierend regeln',
    gering: 'Gering — Hinweis genuegt',
};

/**
 * Abbildung auf `MutualHazard.severity` (`src/types.ts`). Der Bestand kennt
 * nur `low|medium|high`; wer den Katalog spaeter in die vorhandene
 * Gefaehrdungsmatrix uebertraegt, soll dieselbe Zuordnung benutzen und nicht
 * jedes Mal neu entscheiden.
 */
export const SCHWERE_ZU_SEVERITY: Record<Schwere, 'low' | 'medium' | 'high'> = {
    hoch: 'high',
    mittel: 'medium',
    gering: 'low',
};

/**
 * Raeumliche Voraussetzung. Eine zeitliche Ueberschneidung allein reicht fuer
 * die meisten Paarungen NICHT — zwei Gewerke in verschiedenen Bauteilen
 * gefaehrden sich nicht gegenseitig, nur weil sie im selben Monat arbeiten.
 * Ohne dieses Feld erzeugt die Analyse Papier statt Sicherheit.
 *
 * `uebereinander`  — nur bei Arbeiten uebereinander in derselben Achse.
 * `gleicher_bereich` — nur im selben Bauabschnitt, Raum oder Geschoss.
 * `benachbart`     — wirkt ueber die Bereichsgrenze hinweg (Staub, Laerm,
 *                    Faser, Brand, Truemmerflug, Verkehr).
 * `baustellenweit` — gilt unabhaengig vom Ort, weil eine gemeinsam genutzte
 *                    Einrichtung oder ein Verkehrsweg betroffen ist.
 */
export type RaumBezug = 'uebereinander' | 'gleicher_bereich' | 'benachbart' | 'baustellenweit';

export const RAUM_LABEL: Record<RaumBezug, string> = {
    uebereinander: 'nur bei Arbeiten übereinander',
    gleicher_bereich: 'nur im selben Bereich',
    benachbart: 'auch in angrenzenden Bereichen',
    baustellenweit: 'baustellenweit',
};

export interface GewerkePaarung {
    /** Unveraenderlich, siehe Dateikopf. */
    code: string;
    /**
     * Die beiden beteiligten Gewerke. `a` und `b` sind KEINE Richtung
     * (Verursacher/Betroffener), sondern zwei Teilnehmer — siehe
     * `findePaarung`. `a === b` ist zulaessig und meint zwei Arbeitgeber
     * desselben Gewerks.
     */
    a: GewerkId;
    b: GewerkId;
    schwere: Schwere;
    raumbezug: RaumBezug;
    /** Was konkret passiert, wenn beide gleichzeitig arbeiten. */
    gefaehrdung: string;
    /** Die koordinierende Massnahme des SiGeKo — nicht die Schutzmassnahme
     *  des einzelnen Unternehmers. */
    massnahme: string;
    /** Nur gesicherte Fundstellen. Im Zweifel bleibt die Liste kurz oder
     *  leer — eine erfundene Fundstelle ist im SiGe-Plan schlimmer als gar
     *  keine, weil sie niemand mehr nachprueft. */
    vorschriften: string[];
}

const p = (
    code: string,
    a: GewerkId,
    b: GewerkId,
    schwere: Schwere,
    raumbezug: RaumBezug,
    gefaehrdung: string,
    massnahme: string,
    vorschriften: string[] = [],
): GewerkePaarung => ({ code, a, b, schwere, raumbezug, gefaehrdung, massnahme, vorschriften });

export const PAARUNGEN: GewerkePaarung[] = [

    // ─── Kranbetrieb ──────────────────────────────────────────────────────
    p('GK-001', 'kran', 'rohbau', 'hoch', 'uebereinander',
        'Der Kran führt Schalungstische, Bewehrungskörbe und Betonkübel über Arbeitsplätze des Rohbaus. Versagt das Anschlagmittel oder pendelt die Last beim Abbremsen, trifft sie Beschäftigte, die darunter schalen, bewehren oder betonieren.',
        'Lastwege im Baustelleneinrichtungsplan festlegen und so führen, dass sie besetzte Arbeitsplätze nicht überstreichen. Ist das baulich nicht möglich, wird der Bereich unter dem Lastweg für die Dauer des Hebevorgangs geräumt und abgesperrt; Anschlag- und Absetzplätze je Bauabschnitt vereinbaren, Einweiser mit Sichtkontakt zum Kranführer.',
        ['ASR A2.1', 'DGUV Vorschrift 52', '§ 3 Abs. 3 BaustellV']),

    p('GK-002', 'kran', 'geruestbau', 'hoch', 'gleicher_bereich',
        'Die Last schwenkt gegen das Gerüst oder streift Beläge und Verankerungen, während die Gerüstkolonne auf derselben Fassadenseite auf- oder umbaut. Der Anprall kann Gerüstlagen aus der Verankerung reißen und die Kolonne mitreißen.',
        'Kranbetrieb im betroffenen Gerüstabschnitt während Auf-, Um- und Abbau einstellen (zeitliche Trennung). Wo das nicht geht, Lastweg mit Mindestabstand zur Gerüstflucht festlegen, ständige Funkverbindung zwischen Kranführer und Kolonnenführer, gemeinsamer Windgrenzwert mit Abbruchkriterium.',
        ['DGUV Vorschrift 52', 'TRBS 2121 Teil 1', '§ 3 Abs. 3 BaustellV']),

    p('GK-003', 'kran', 'zimmerer_dachdecker', 'hoch', 'uebereinander',
        'Dachbinder, Sparren und Fertigteile werden über die Dachfläche eingeschwenkt, während dort bereits Zimmerer arbeiten. Großflächige Bauteile bekommen bei Wind Eigenbewegung, das Ausrichten von Hand erfolgt an der Absturzkante.',
        'Montagereihenfolge so festlegen, dass sich unter und neben dem eingeschwenkten Bauteil niemand aufhält; Absturzsicherung und Anschlagpunkte müssen vor dem ersten Element stehen. Windgeschwindigkeitsgrenze und Abbruchkriterium schriftlich vereinbaren und einer Person die Entscheidung zuweisen.',
        ['Anhang II Nr. 10 BaustellV', 'DGUV Vorschrift 52', 'TRBS 2121 Teil 1']),

    p('GK-004', 'kran', 'strassenbau', 'hoch', 'benachbart',
        'Der Schwenkbereich reicht über die öffentliche Verkehrsfläche, auf der gleichzeitig die Straßenbaukolonne und der Restverkehr geführt werden. Herabfallende Last trifft Beschäftigte und Dritte; die Kranaufstellung selbst verengt die Fahrbahn.',
        'Schwenkbereich technisch begrenzen; Hübe über der Verkehrsfläche nur in behördlich angeordneten Sperrzeiten. Kranstellfläche, Verkehrsführung und Fußgängerumleitung gemeinsam planen und in der verkehrsrechtlichen Anordnung abbilden.',
        ['RSA 21', '§ 45 StVO', 'ASR A5.2']),

    p('GK-005', 'kran', 'elektro', 'hoch', 'gleicher_bereich',
        'Ausleger, Lastseil oder Anschlagmittel nähern sich einer Freileitung oder der provisorischen Freileitung der Baustromversorgung. Der Stromübertritt trifft Kranführer, Anschläger und die Elektrofachkraft an der Speisestelle gleichzeitig.',
        'Freileitung durch den Netzbetreiber abschalten oder abdecken lassen, bevor der Kran aufgestellt wird. Ist das nicht erreichbar, Schutzabstand nach Spannungsebene ermitteln, den Gefahrbereich körperlich absperren und die Kranstellung außerhalb der Reichweite wählen; Einweiser mit alleiniger Abbruchbefugnis.',
        ['Anhang II Nr. 4 BaustellV', 'DGUV Vorschrift 3', 'DGUV Vorschrift 52']),

    p('GK-006', 'kran', 'erdbau', 'hoch', 'gleicher_bereich',
        'Kranstellfläche und Abstützung liegen an der Kante der noch offenen Baugrube, während der Aushub weiterläuft. Der Aushub entzieht dem Stützfuß den Erdwiderstand — der Kran kippt in die Grube, in der Beschäftigte arbeiten.',
        'Aufstellflächen und Abstände zur Böschungs- bzw. Verbaukante vor Aushubbeginn festlegen und nachweisen lassen. Aushub im Bereich einer belegten Kranstellfläche nur nach ausdrücklicher Abstimmung und erneuter Standsicherheitsprüfung.',
        ['DIN 4124', 'DGUV Vorschrift 52']),

    p('GK-007', 'kran', 'aufzugsbau', 'hoch', 'uebereinander',
        'Lasten werden über den offenen Aufzugsschacht oder die Schachtkopföffnung transportiert, während Monteure im Schacht oder auf dem Fahrkorbdach arbeiten. Herabfallendes Material hat im Schacht keinen Ausweichraum.',
        'Schachtöffnungen während des Kranbetriebs geschlossen und tragfähig abgedeckt halten; Schachtmontage und Hübe über dem Schacht zeitlich trennen. Eine Verständigungsregel festlegen, mit der die Montagekolonne den Kranbetrieb über dem Schacht sperren kann.',
        ['ASR A2.1', 'DGUV Vorschrift 52']),

    p('GK-008', 'kran', 'kran', 'hoch', 'gleicher_bereich',
        'Zwei oder mehr Krane auf derselben Baustelle haben überschneidende Schwenkbereiche. Kollidieren Ausleger, Lastseile oder Lasten, fallen Bauteile in besetzte Bereiche und ein Kran kann versagen.',
        'Höhenstaffelung und Anti-Kollisionssystem verlangen, Vorrangregelung schriftlich festlegen und beiden Kranführern bekannt geben. Gemeinsame Funkfrequenz, gemeinsame Betriebsanweisung für den Mehrkranbetrieb, Regelung für Windfahnenbetrieb außerhalb der Arbeitszeit.',
        ['DGUV Vorschrift 52', '§ 3 Abs. 3 BaustellV']),

    // ─── Gerüst als gemeinsam genutzte Einrichtung ────────────────────────
    p('GK-009', 'rohbau', 'geruestbau', 'hoch', 'gleicher_bereich',
        'Das Gerüst wächst mit dem Rohbau; genutzt werden Lagen, die noch nicht fertiggestellt und nicht freigegeben sind — fehlender Seitenschutz, unvollständiger Belag, fehlende Verankerung.',
        'Auf- und Umbau abschnittsweise VOR der Nutzung als eigenen Vorgang terminieren. Nicht freigegebene Abschnitte körperlich sperren und kennzeichnen; Freigabe schriftlich, mit Datum und Lastklasse, und je nutzendem Gewerk bekannt geben.',
        ['TRBS 2121 Teil 1', 'BetrSichV', 'ASR A1.3']),

    p('GK-010', 'geruestbau', 'maler', 'hoch', 'gleicher_bereich',
        'Der Maler nimmt Seitenschutz und Beläge heraus, um an Fassadenflächen zu kommen, und stellt sie nicht wieder her. Das Gerüst bleibt offen für alle folgenden Nutzer, ohne dass es jemand bemerkt.',
        'Veränderungsverbot schriftlich vereinbaren und am Gerüst aushängen; Änderungen ausschließlich durch den Gerüstersteller nach Anforderung. Nach jeder Änderung Prüfung und erneute Freigabe, bevor ein Gewerk weiterarbeitet.',
        ['TRBS 2121 Teil 1', 'BetrSichV', 'DGUV Vorschrift 38']),

    p('GK-011', 'geruestbau', 'fassade', 'hoch', 'gleicher_bereich',
        'Fassadenelemente lassen sich nur bei geöffnetem Seitenschutz einbauen, und Breiten- bzw. Lastklasse des gestellten Gerüsts passen nicht zur Elementmontage. Die Monteure arbeiten dann an der offenen Kante mit schwerem Bauteil in der Hand.',
        'Gerüst bereits in der Ausschreibung auf Last-, Breiten- und Wandabstandsklasse der Fassadenmontage auslegen. Seitenschutz nur abschnittsweise öffnen, mit vorher ausgewiesenen Anschlagpunkten für PSA gegen Absturz und sofortigem Wiederherstellen nach jedem Element.',
        ['TRBS 2121 Teil 1', 'ASR A2.1']),

    p('GK-012', 'geruestbau', 'zimmerer_dachdecker', 'hoch', 'uebereinander',
        'Das Dachfanggerüst fehlt, steht zu tief, kommt zu spät oder wird abgebaut, bevor die Dacharbeiten beendet sind — weil Gerüstbau und Dachgewerk getrennt beauftragt sind und niemand die Standzeit schuldet.',
        'Dachfanggerüst als eigenständigen Vorgang vor Beginn der Dacharbeiten terminieren und die Standzeit bis zum Abschluss der Dacharbeiten vertraglich sichern. Abbau erst nach schriftlicher Freigabe des Dachgewerks.',
        ['TRBS 2121 Teil 1', 'ASR A2.1', 'DGUV Vorschrift 38']),

    p('GK-013', 'geruestbau', 'strassenbau', 'mittel', 'benachbart',
        'Das Gerüst steht auf Gehweg oder Fahrbahn, in die gleichzeitig die Verkehrsführung der Straßenbaustelle eingreift. Fußgänger werden in den Fahrbahnbereich gedrängt, Gerüstfüße und Anker stehen im Fahrweg.',
        'Gerüststandfläche, Fußgängerführung und Verkehrsführung gemeinsam in einer verkehrsrechtlichen Anordnung abbilden; Schutztunnel oder abgetrennter Gehweg, Anfahrschutz und Beleuchtung. Gerüstabschnitte über der Verkehrsfläche nur in abgestimmten Zeitfenstern auf- und abbauen.',
        ['RSA 21', '§ 45 StVO', 'ASR A5.2']),

    p('GK-014', 'geruestbau', 'abbruch', 'hoch', 'gleicher_bereich',
        'Das Gerüst ist an dem Bauwerk verankert, das gerade zurückgebaut wird. Mit jedem Rückbauschritt verliert es Verankerungsgrund, und herabfallende Trümmer treffen Gerüst und Kolonne.',
        'Verankerungs- und Abbaukonzept gemeinsam festlegen: das Gerüst folgt dem Rückbau von oben nach unten, die Standsicherheit wird nach jedem Rückbauabschnitt geprüft und dokumentiert. Während der Abbrucharbeiten kein Gerüstbetrieb im selben Abschnitt.',
        ['TRBS 2121 Teil 1', 'DGUV Vorschrift 38']),

    p('GK-015', 'geruestbau', 'geruestbau', 'hoch', 'gleicher_bereich',
        'Zwei Gerüstersteller arbeiten an einem Bauwerk. An den Übergangsstellen fühlt sich niemand zuständig, Bauteile verschiedener Systeme werden gemischt, und die Freigabe gilt jeweils nur für den eigenen Abschnitt.',
        'Gerüstabschnitte eindeutig zuordnen und mit dem Namen des Erstellers kennzeichnen; Übergangsstellen ausdrücklich einem Ersteller zuweisen. Mischen von Bauteilen verschiedener Hersteller untersagen und die Freigabekennzeichnung baustellenweit einheitlich regeln.',
        ['TRBS 2121 Teil 1', 'BetrSichV', 'DGUV Vorschrift 1 § 6']),

    // ─── Rohbau, Erdbau, Verschüttung ─────────────────────────────────────
    p('GK-016', 'rohbau', 'zimmerer_dachdecker', 'hoch', 'uebereinander',
        'Dachstuhl- und Dachdeckerarbeiten laufen über noch offenen Geschossdecken, während der Rohbau darunter weiterarbeitet. Ziegel, Latten und Werkzeug fallen durch Deckenöffnungen; nicht tragfähige Bauteile werden durchtreten.',
        'Bereich unter der bearbeiteten Dachfläche für die Dauer der Arbeiten sperren oder mit Schutzdach bzw. Fanglage sichern. Deckenöffnungen tragfähig abdecken und gegen Verschieben sichern, Materialtransport nicht über offene Kanten führen.',
        ['ASR A2.1', 'DGUV Vorschrift 38']),

    p('GK-017', 'rohbau', 'erdbau', 'hoch', 'gleicher_bereich',
        'Fundament- und Schalarbeiten finden in der Baugrube oder im Graben statt, während daneben ausgehoben, verfüllt oder an der Kante gefahren wird. Es droht Verschüttung, abrutschende Böschung und Anfahren durch das Erdbaugerät.',
        'Aushub und Arbeiten in derselben Baugrubensohle zeitlich trennen. Böschungswinkel oder Verbau vor dem ersten Betreten nachweisen lassen; Sicherheitsabstand zur Kante für Fahrzeuge, Aushub- und Materiallagerung festlegen und vor Ort markieren.',
        ['DIN 4124', 'Anhang II Nr. 1 BaustellV', 'DGUV Vorschrift 38']),

    p('GK-018', 'rohbau', 'elektro', 'hoch', 'uebereinander',
        'Für Elektrotrassen werden Deckendurchbrüche und Kernbohrungen hergestellt, während darunter der Rohbau arbeitet. Bohrkerne und Bohrwasser fallen ins darunterliegende Geschoss, und die Öffnungen bleiben ungesichert zurück.',
        'Durchbrüche vor Beginn der darunterliegenden Arbeiten herstellen; ist das nicht möglich, den Bereich unter der Bohrstelle absperren und Bohrkerne sichern. Jede Öffnung sofort nach Herstellung tragfähig abdecken — die Zuständigkeit dafür namentlich festlegen, nicht "dem Verursacher" überlassen.',
        ['ASR A2.1', 'DGUV Vorschrift 38']),

    p('GK-019', 'rohbau', 'shk', 'hoch', 'uebereinander',
        'Schlitz- und Kernbohrarbeiten der Haustechnik im frisch erstellten Rohbau treffen Bewehrung oder schwächen tragende Bauteile; Bohrkern und Bohrwasser gelangen in belegte Bereiche darunter.',
        'Durchbruchsplanung vor der Betonage abstimmen, damit nachträgliche Bohrungen die Ausnahme bleiben; nachträgliche Bohrungen nur mit Freigabe des Tragwerksplaners. Bereich unter der Bohrstelle absperren, Bohrkernsicherung und Wasserfassung verlangen.',
        ['ASR A2.1', 'DGUV Vorschrift 38']),

    p('GK-020', 'rohbau', 'strassenbau', 'mittel', 'baustellenweit',
        'Betonanlieferung, Betonpumpe und Fertigteiltransporte belegen die Zufahrt, die gleichzeitig Arbeitsbereich und Rettungsweg der Straßenbaukolonne ist. Es wird rückwärts rangiert, wo Beschäftigte zu Fuß gehen.',
        'Anlieferungs- und Pumpenzeiten in einem Belegungsplan festlegen und Aufstellflächen ausweisen. Fuß- und Fahrwege trennen, Rückwärtsfahren nur mit Einweiser; Rettungs- und Feuerwehrzufahrten in jeder Bauphase freihalten und das im SiGe-Plan verorten.',
        ['DGUV Vorschrift 38 § 4', 'ASR A1.8', 'ASR A2.3']),

    p('GK-021', 'rohbau', 'rohbau', 'mittel', 'uebereinander',
        'Mehrere Rohbaufirmen oder Baulose arbeiten in verschiedenen Geschossen desselben Bauwerks. Wer die Absturzsicherung an Treppenaugen, Schächten und Deckenrändern stellt und erhält, ist nicht geregelt — sie verschwindet mit dem Los, das sie gestellt hat.',
        'Bauabschnitte räumlich eindeutig abgrenzen und Arbeiten übereinander in derselben Achse ausschließen. Kollektive Absturzsicherungen einem Unternehmer als Vorhalteleistung zuweisen, mit Standzeit bis zum benannten Termin.',
        ['§ 3 Abs. 3 BaustellV', 'DGUV Vorschrift 1 § 6', 'ASR A2.1']),

    p('GK-022', 'erdbau', 'strassenbau', 'hoch', 'benachbart',
        'Aushub oder Leitungsgraben liegen unmittelbar neben dem fließenden Verkehr. Beschäftigte werden angefahren, Fahrzeuge stürzen in den Graben, und die verbleibende Arbeitsbreite unterschreitet das zulässige Maß.',
        'Verkehrssicherung nach RSA 21 mit Schutzeinrichtung statt bloßer Baken; Mindestbreiten für Arbeitsplätze und Verkehrswege nachweisen, sonst Fahrstreifen sperren statt verengen. Warnkleidung, Zugang zur Arbeitsstelle nur von der verkehrsabgewandten Seite.',
        ['RSA 21', 'ASR A5.2', '§ 45 StVO']),

    p('GK-023', 'erdbau', 'elektro', 'hoch', 'gleicher_bereich',
        'Das Erdbaugerät trifft ein erdverlegtes Kabel. Der Lichtbogen trifft Baggerführer, Einweiser und die in der Nähe arbeitende Elektrofachkraft gleichzeitig; die Ausfallzeit der Versorgung trifft die gesamte Baustelle.',
        'Spartenauskunft aller Versorger und Ortung vor dem ersten Aushub; Trassen im Gelände einmessen und markieren. Im Leitungsschutzstreifen von Hand schachten, Freischaltung und schriftliche Freimeldung des Netzbetreibers zur Voraussetzung für die Aufgrabungsfreigabe machen.',
        ['DGUV Vorschrift 3', 'DGUV Vorschrift 38', 'DGUV Information 203-006']),

    p('GK-024', 'erdbau', 'shk', 'hoch', 'gleicher_bereich',
        'Beim Aushub wird eine Gas-, Fernwärme- oder Wasserleitung getroffen, während die Haustechnik im selben Graben montiert. Es drohen Verbrühung, Ausströmen von Gas und eine zündfähige Atmosphäre im Graben, aus dem der Rückweg lang ist.',
        'Leitungsauskunft, Ortung und Handschachtung im Schutzstreifen; Absperr- und Freischaltbestätigung des Versorgers vor Beginn. Zündquellenverbot im Graben, schriftliche Alarm- und Räumungsregel für Gasaustritt, zweiter Aufstieg in ausreichender Nähe.',
        ['DGUV Vorschrift 38', '§ 3 Abs. 3 BaustellV']),

    p('GK-025', 'erdbau', 'galabau', 'mittel', 'gleicher_bereich',
        'Der Landschaftsbau arbeitet mit Fahrzeugen und Material an der Kante der noch offenen Baugrube oder auf dem frisch verfüllten, nicht verdichteten Bereich. Die Kante bricht unter der Auflast.',
        'Kantenbereich bis zum Verfüllen und Verdichten sperren und die Lasten- und Fahrverbotszone vor Ort kennzeichnen. Oberboden- und Pflanzarbeiten erst nach Verfüllung und Verdichtungsnachweis freigeben.',
        ['DIN 4124']),

    p('GK-026', 'erdbau', 'erdbau', 'mittel', 'gleicher_bereich',
        'Zwei Tiefbaufirmen arbeiten in derselben Baugrube. Die eine verändert Böschung oder Verbau, auf die sich die andere verlässt — Aussteifungen werden für die eigene Zufahrt entfernt.',
        'Verantwortung für Verbau und Böschung einem Unternehmer zuweisen; Änderungen nur durch ihn und nur nach erneutem Nachweis. Gemeinsame tägliche Kurzabstimmung, solange beide in derselben Grube arbeiten.',
        ['DIN 4124', 'DGUV Vorschrift 1 § 6']),

    // ─── Abbruch und Schadstoffe ──────────────────────────────────────────
    p('GK-027', 'abbruch', 'elektro', 'hoch', 'gleicher_bereich',
        'Im Rückbaubereich liegen noch aktive Leitungen. Trennschleifer oder Abbruchgerät treffen die spannungsführende Installation, oder die Baustromversorgung der übrigen Gewerke ist durch den Rückbaubereich geführt.',
        'Freischaltung des Rückbaubereichs durch eine Elektrofachkraft mit schriftlicher Freimeldung an das Abbruchunternehmen vor Beginn — die fünf Sicherheitsregeln sind Teil der Freigabe. Provisorische Baustromversorgung außerhalb des Rückbaubereichs führen, damit sie nicht mit abgerissen wird.',
        ['DGUV Vorschrift 3', 'DGUV Information 203-006', 'DGUV Vorschrift 38']),

    p('GK-028', 'abbruch', 'strassenbau', 'hoch', 'benachbart',
        'Trümmerflug, Staub und Erschütterung wirken auf die angrenzende Verkehrsfläche, auf der die Straßenbaukolonne arbeitet. Der Gefahrbereich des Abbruchs endet nicht an der Grundstücksgrenze.',
        'Sperrbereich in Abhängigkeit von Bauteilhöhe und Abbruchverfahren festlegen und mit Schutzwand oder Schutzgerüst sichern. Abbruchvorgänge mit hoher Auswurfweite nur in behördlich angeordneten Sperrzeiten; Staubbindung durch Berieselung und Straßenreinigung.',
        ['ASR A2.1', 'RSA 21', '§ 45 StVO']),

    p('GK-029', 'abbruch', 'erdbau', 'hoch', 'gleicher_bereich',
        'Rückbau und Aushub laufen gleichzeitig in derselben Achse. Bauteile stürzen in die offene Baugrube, und der Aushub entzieht dem stehengebliebenen Bauwerksteil die Gründung.',
        'Abbruchanweisung und Aushubfolge aufeinander abstimmen und für die Zwischenzustände einen gemeinsamen Standsicherheitsnachweis verlangen. Keine gleichzeitigen Arbeiten in derselben Achse; Übergabe zwischen den Gewerken schriftlich festhalten.',
        ['DGUV Vorschrift 38', 'DIN 4124']),

    p('GK-030', 'abbruch', 'trockenbau', 'mittel', 'benachbart',
        'Lärm, Staub und Erschütterung aus dem Rückbau wirken in den bereits ausgebauten Nachbarbereich. Zuruf und Warnsignale gehen im Lärm unter, Quarzstaub verteilt sich über die Öffnungen.',
        'Rückbau und Ausbau in getrennte Bauabschnitte legen und staubdicht abschotten; laute Verfahren in Zeitfenster außerhalb der Ausbauarbeiten legen. Verständigung optisch statt durch Zuruf regeln, Lärmbereiche kennzeichnen.',
        ['LärmVibrationsArbSchV', 'TRGS 559', 'DGUV Regel 112-194']),

    p('GK-031', 'abbruch', 'shk', 'hoch', 'gleicher_bereich',
        'Beim Rückbau wird brenn- und trennschneidend gearbeitet, während Leitungen und Behälter der Haustechnik noch gefüllt sind — Gas, Heizöl, Kältemittel. Es droht Brand oder Explosion in einem Bereich mit erschwerter Flucht.',
        'Anlagen vor Rückbaubeginn entleeren, spülen und schriftlich freimelden lassen; ohne Freimeldung gilt Zündquellenverbot. Heißarbeiten nur mit Erlaubnisschein, Brandwache während und nach den Arbeiten sowie festgelegter Nachkontrolle.',
        ['TRGS 528', 'ASR A2.2', 'BetrSichV']),

    p('GK-032', 'abbruch', 'schadstoff', 'hoch', 'gleicher_bereich',
        'Der Rückbau beginnt, bevor die Schadstoffsanierung abgeschlossen und freigemessen ist. Das Abbruchgerät setzt Fasern aus Bauteilen frei, die als saniert galten oder nie untersucht wurden.',
        'Harte Reihenfolge im Terminplan: Erkundung, Sanierung, Freimessung, dann Rückbau — keine Parallelität im selben Bauteil. Stopp-Regel und Meldeweg für unerwartete Funde vorher schriftlich festlegen, einschließlich Kostenträger und Terminfolgen.',
        ['TRGS 519', 'TRGS 524', 'Anhang II Nr. 2 BaustellV', 'DGUV Vorschrift 38']),

    p('GK-033', 'abbruch', 'abbruch', 'hoch', 'gleicher_bereich',
        'Zwei Rückbauunternehmen arbeiten im selben Bauwerk. Jedes plant seine Standsicherheit für sich, und der Rückbauschritt des einen entzieht dem Arbeitsbereich des anderen die Aussteifung.',
        'Eine gemeinsame Abbruchanweisung für das gesamte Bauwerk verlangen, mit benannter Reihenfolge und Freigabepunkten zwischen den Losen. Gleichzeitiges Arbeiten in derselben Achse und in übereinanderliegenden Geschossen ausschließen.',
        ['DGUV Vorschrift 38', 'DGUV Vorschrift 1 § 6']),

    p('GK-034', 'schadstoff', 'trockenbau', 'hoch', 'benachbart',
        'Der Ausbau grenzt unmittelbar an den Schwarzbereich. Ein Durchbruch oder eine geöffnete Tür bricht die Unterdruckhaltung, und Fasern gelangen in den Bereich, in dem ohne Schutzausrüstung gearbeitet wird.',
        'Schwarzbereich räumlich UND zeitlich vom übrigen Ausbau trennen; Zugänge ausschließlich über Schleusen. Bereichsgrenzen im SiGe-Plan verorten und vor Ort kennzeichnen; angrenzende Bereiche erst nach Freimessung wieder freigeben.',
        ['TRGS 519', 'GefStoffV § 15', 'Anhang II Nr. 2 BaustellV']),

    p('GK-035', 'schadstoff', 'elektro', 'hoch', 'gleicher_bereich',
        'Die Elektrofachkraft muss für Strom und Beleuchtung in den Sanierungsbereich, ohne Sachkunde nach TRGS 519, ohne passende Schutzausrüstung und ohne in der Arbeitsplanung des Sanierungsunternehmens aufzutauchen.',
        'Strom- und Beleuchtungsversorgung des Sanierungsbereichs vor dessen Einrichtung fertigstellen und von außen schaltbar führen, damit während der Sanierung niemand hinein muss. Unvermeidbaren Zutritt nur mit Sachkunde und Anmeldung beim Sanierungsunternehmen.',
        ['TRGS 519', 'DGUV Vorschrift 3', 'GefStoffV § 15']),

    p('GK-036', 'schadstoff', 'shk', 'hoch', 'gleicher_bereich',
        'Rohrleitungsdämmung, Dichtungen und Brandschutzmanschetten im Bestand sind asbesthaltig. Die Haustechnik trennt Leitungen an und setzt dabei Fasern frei — für sich und für alle im selben Geschoss.',
        'Bestandsleitungen vor jeder Demontage beproben lassen und die Trennstellen im Plan festlegen. Demontage kontaminierter Abschnitte ausschließlich durch das Sanierungsunternehmen; die Haustechnik beginnt erst nach Freimessung des Abschnitts.',
        ['TRGS 519', 'GefStoffV', 'GefStoffV § 15']),

    p('GK-037', 'schadstoff', 'reinigung', 'hoch', 'gleicher_bereich',
        'Die Bauendreinigung arbeitet in einem Bereich, der noch nicht freigemessen ist. Trockenes Kehren und Aufwirbeln erzeugt genau die Exposition, welche die Sanierung verhindern sollte — bei einem Gewerk ohne jede Schutzausrüstung.',
        'Reinigung erst nach schriftlicher Freimessung freigeben und den Bereich bis dahin verschlossen halten. Klarstellen, dass die Feinreinigung im Sanierungsbereich Leistung des Sanierungsunternehmens ist und nicht der Bauendreinigung.',
        ['TRGS 519', 'GefStoffV']),

    // ─── Feuchte, Strom und Estrich ───────────────────────────────────────
    p('GK-038', 'estrich', 'elektro', 'hoch', 'gleicher_bereich',
        'Nassestrich und Trocknungsfeuchte treffen auf Baustromleitungen, Verteiler und bereits gesetzte Dosen im Bodenbereich. Nässe und Strom im selben Raum, während gleichzeitig gearbeitet wird.',
        'Baustromverteiler und Leitungen vor dem Estricheinbau aus dem Bodenbereich nehmen oder erhöht führen; Speisung ausschließlich über Fehlerstromschutzeinrichtungen. Elektroarbeiten im Estrichfeld für die Dauer von Einbau und Trocknung einstellen.',
        ['DGUV Vorschrift 3', 'DGUV Information 203-006']),

    p('GK-039', 'estrich', 'shk', 'mittel', 'gleicher_bereich',
        'Fußbodenheizung und Leitungen liegen im Aufbau, der Estrich wird vor der Druckprobe eingebracht oder die Rohre werden beim Einbringen beschädigt. Der Schaden zeigt sich erst nach dem Aufheizen, unter fertigem Belag.',
        'Druckprobe mit Protokoll zur Freigabevoraussetzung für den Estricheinbau machen und im Terminplan als harte Abhängigkeit führen. Leitungslage vor dem Einbringen dokumentieren; danach gilt Bohrverbot im Bodenaufbau ohne vorherige Ortung.',
        ['§ 3 Abs. 3 BaustellV']),

    p('GK-040', 'estrich', 'trockenbau', 'mittel', 'gleicher_bereich',
        'Der Trockenbau betritt und belastet frischen Estrich, während Trocknungsgeräte laufen. Es entstehen Stolper- und Sturzstellen, Zugluft und Dauerlärm, und die Geräte hängen an derselben provisorischen Stromversorgung.',
        'Sperrfrist je Estrichfeld mit Datum kennzeichnen und als eigenen Vorgang im Terminplan führen. Transportwege über Laufstege festlegen, Standorte und Stromversorgung der Trocknungsgeräte abstimmen.',
        ['ASR A1.8']),

    p('GK-041', 'estrich', 'fliesenleger', 'mittel', 'gleicher_bereich',
        'Der Fliesenleger arbeitet auf noch nicht belegreifem Estrich, und das Schleifen oder Fräsen der Untergrundvorbereitung erzeugt quarzhaltigen Staub für beide Gewerke im selben Raum.',
        'Belegreife messen, dokumentieren und erst dann freigeben. Schleif- und Fräsarbeiten nur mit Absaugung und nur, wenn kein anderes Gewerk im Raum ist — Staubarbeiten in Randzeiten legen.',
        ['TRGS 559', 'GefStoffV']),

    // ─── Elektro gegen die Ausbaugewerke ──────────────────────────────────
    p('GK-042', 'elektro', 'trockenbau', 'hoch', 'gleicher_bereich',
        'Leitungen liegen im Ständerwerk, während beplankt wird. Schrauben treffen Leitungen, und die Elektrofachkraft arbeitet an offener Installation in derselben Wand, an der geschraubt wird.',
        'Trassen vor der Beplankung dokumentieren und Schraubverbotszonen markieren. Die Anlage bis zur Fertigstellung der Beplankung spannungsfrei halten und freimelden; die Reihenfolge im Terminplan als harte Abhängigkeit hinterlegen.',
        ['DGUV Vorschrift 3', 'DGUV Information 203-006']),

    p('GK-043', 'elektro', 'fliesenleger', 'hoch', 'gleicher_bereich',
        'Im Nassbereich wird in Wände gebohrt und geschlitzt, in denen bereits Leitungen liegen; gleichzeitig wird mit Wasser gearbeitet und an offenen Dosen hantiert.',
        'Leitungsverlauf vor Beginn ausweisen und orten. Die betroffenen Stromkreise für die Dauer der Fliesenarbeiten freischalten und freimelden; Wiederzuschalten erst nach Fertigstellung und nur durch eine benannte Person.',
        ['DGUV Vorschrift 3', 'DGUV Information 203-006']),

    p('GK-044', 'elektro', 'reinigung', 'hoch', 'gleicher_bereich',
        'Die Bauendreinigung arbeitet nass an einer teils provisorischen, teils bereits unter Spannung stehenden, aber noch nicht abgenommenen Installation.',
        'Unter Spannung stehende Bereiche vor Reinigungsbeginn benennen und kennzeichnen; Nassreinigung erst nach Abnahme oder mit Schutzabdeckung. Reinigungsgeräte nur an ausgewiesene, fehlerstromgeschützte Anschlusspunkte.',
        ['DGUV Vorschrift 3', 'DGUV Information 203-006']),

    p('GK-045', 'elektro', 'maler', 'mittel', 'gleicher_bereich',
        'Spritz- und Nassschliffarbeiten laufen an offener Installation; lösemittelhaltige Dämpfe sammeln sich an Betriebsmitteln, die noch unter Spannung stehen.',
        'Stromkreise im Arbeitsbereich freischalten und freimelden, Betriebsmittel abdecken. Bei Spritzarbeiten Lüftung und Zündquellenfreiheit gemeinsam festlegen und die Arbeiten zeitlich von Schalthandlungen trennen.',
        ['DGUV Vorschrift 3', 'GefStoffV']),

    p('GK-046', 'elektro', 'aufzugsbau', 'hoch', 'gleicher_bereich',
        'Die Aufzugssteuerung wird zugeschaltet oder eine Probefahrt gestartet, während Monteure im Schacht, in der Grube oder auf dem Fahrkorbdach arbeiten.',
        'Schaltberechtigung und Freigabe für Probefahrten schriftlich einer Person zuweisen. Schachtarbeiten nur mit verriegelter Steuerung (Schlüsselschalter, personengebundene Sperre) und schriftlicher Freimeldung; Probefahrten nur bei nachweislich geräumtem Schacht.',
        ['DGUV Vorschrift 3', 'BetrSichV', 'TRBS 3121']),

    p('GK-047', 'elektro', 'galabau', 'hoch', 'gleicher_bereich',
        'Pflanzgruben, Baumpflanzungen und Tiefenlockerung in den Außenanlagen liegen über erdverlegten Kabeln, deren Lage im Bestandsplan der Freianlagen nicht auftaucht.',
        'Bestandsplan mit allen Trassen vor Beginn übergeben und die Trassen im Gelände markieren. Im Schutzstreifen Handschachtung; bei Arbeiten unmittelbar an der Trasse Freischaltung und Freimeldung durch den Netzbetreiber.',
        ['DGUV Vorschrift 3', 'DGUV Vorschrift 38']),

    p('GK-048', 'elektro', 'elektro', 'hoch', 'gleicher_bereich',
        'Zwei Elektrounternehmen arbeiten an derselben Anlage. Das eine schaltet zu, während das andere an einem Stromkreis arbeitet, den es selbst freigeschaltet und gesichert glaubt.',
        'Einen Schaltberechtigten für die Baustelle benennen und eine zentrale Schaltberechtigungsliste führen. Sperrung ausschließlich mit personengebundenen Schlössern; Freimeldung immer schriftlich, mündliche Freigaben ausdrücklich untersagen.',
        ['DGUV Vorschrift 3', 'DGUV Information 203-006', 'DGUV Vorschrift 1 § 6']),

    // ─── Brandlast und Heißarbeiten ───────────────────────────────────────
    p('GK-049', 'shk', 'trockenbau', 'hoch', 'gleicher_bereich',
        'Löt- und Schweißarbeiten an Rohrleitungen finden neben Dämmstoffen, Holzständern und Dampfsperren statt. Glutnester entstehen in Hohlräumen, die kurz darauf verschlossen werden, und zeigen sich erst Stunden später.',
        'Heißarbeiten nur mit Erlaubnisschein, Abschottung der Hohlräume und Brandwache während sowie mindestens eine Stunde nach den Arbeiten. Pressverbindungen statt Löten vorgeben, wo technisch möglich; Verschließen der Hohlräume erst nach Freigabe der Nachkontrolle.',
        ['TRGS 528', 'ASR A2.2', 'BetrSichV']),

    p('GK-050', 'shk', 'maler', 'hoch', 'gleicher_bereich',
        'Zündquellen aus Heißarbeiten treffen auf lösemittelhaltige Lacke, Kleber und Grundierungen. In geschlossenen, schlecht belüfteten Räumen kann sich eine explosionsfähige Atmosphäre bilden.',
        'Heißarbeiten und Beschichtungsarbeiten im selben Raum strikt zeitlich trennen und die Reihenfolge im Belegungsplan festhalten. Lüftung vor und während der Beschichtungsarbeiten sicherstellen, Lagerorte lösemittelhaltiger Stoffe außerhalb des Bereichs mit Heißarbeiten festlegen.',
        ['TRGS 528', 'GefStoffV', 'ASR A2.2']),

    p('GK-051', 'shk', 'shk', 'mittel', 'gleicher_bereich',
        'Zwei Haustechnikfirmen arbeiten an demselben Strang. Die eine setzt eine Druckprobe an oder nimmt in Betrieb, während die andere noch montiert.',
        'Verantwortung für Absperrung, Befüllung und Inbetriebnahme je Strang einem Unternehmer zuweisen und Schaltzustände dokumentieren. Druckproben und Inbetriebnahmen ankündigen und den betroffenen Abschnitt vorher räumen.',
        ['BetrSichV', '§ 3 Abs. 3 BaustellV']),

    p('GK-052', 'zimmerer_dachdecker', 'maler', 'hoch', 'benachbart',
        'Flämm- und Heißarbeiten der Dachabdichtung laufen über oder neben Beschichtungsarbeiten mit brennbaren Stoffen, auf einer Konstruktion, die selbst Brandlast ist.',
        'Flämmarbeiten und Beschichtungsarbeiten zeitlich trennen; Holzbauteile im Flämmbereich brandschutztechnisch abdecken. Erlaubnisschein, Brandwache und Nachkontrolle festlegen, Lagerorte brennbarer Stoffe außerhalb des Flämmbereichs.',
        ['ASR A2.2', 'BetrSichV', 'DGUV Vorschrift 38']),

    p('GK-053', 'zimmerer_dachdecker', 'fassade', 'hoch', 'uebereinander',
        'Dach- und Fassadenarbeiten laufen übereinander an derselben Gebäudeseite und nutzen dasselbe Gerüst. Herabfallendes Material trifft die Fassadenmonteure, und beide Gewerke beanspruchen dieselben Gerüstlagen für Material.',
        'Höhenversetzte Arbeiten in derselben Achse ausschließen — zeitlich oder durch seitliche Trennung der Abschnitte. Wo das nicht geht, Schutzdach oder Fanglage; Gerüstbelegung und Materialtransport in einem Belegungsplan regeln.',
        ['ASR A2.1', 'TRBS 2121 Teil 1']),

    p('GK-054', 'fassade', 'strassenbau', 'hoch', 'benachbart',
        'Fassadenelemente, Werkzeug und Befestigungsmittel werden über Gehweg oder Fahrbahn montiert, auf der gleichzeitig gearbeitet und Verkehr geführt wird.',
        'Sperrbereich unter der Montagestelle einrichten und den Gehweg mit Schutzdach überbauen. Montagen über Verkehrsflächen nur in angeordneten Sperrzeiten; Fußgängerführung vor Beginn festlegen und nicht der Kolonne überlassen.',
        ['ASR A2.1', 'RSA 21', '§ 45 StVO']),

    p('GK-055', 'fassade', 'galabau', 'hoch', 'uebereinander',
        'Die Außenanlagen werden unter der Fassade hergestellt, während dort noch montiert, abgedichtet oder gereinigt wird. Herabfallende Teile treffen Beschäftigte, die nach unten schauen.',
        'Außenanlagen in der betroffenen Achse erst nach Abschluss der Fassadenarbeiten beginnen. Ist das terminlich nicht haltbar, Sperrbereich mit Bauzaun und Schutzdach einrichten und die Grenze im SiGe-Plan verorten.',
        ['ASR A2.1']),

    // ─── Schacht, Aufzug und Ausbau ───────────────────────────────────────
    p('GK-056', 'aufzugsbau', 'rohbau', 'hoch', 'uebereinander',
        'Material und Werkzeug fallen durch ungesicherte Schachtöffnungen auf die Monteure im Schacht. Der Rohbau nutzt die Schachtöffnungen als Durchreiche oder Abwurf.',
        'Schachtzugänge und -öffnungen in jedem Geschoss dauerhaft sichern, kennzeichnen und mit Schlüsselregelung versehen. Während der Schachtmontage den Bereich über dem Schacht sperren; Abwurf jeder Art in Schächten ausdrücklich untersagen.',
        ['ASR A2.1', 'DGUV Vorschrift 38', 'BetrSichV']),

    p('GK-057', 'aufzugsbau', 'trockenbau', 'mittel', 'gleicher_bereich',
        'Schachtwände und Vorsatzschalen werden beplankt, solange die Türöffnungen offen sind; beide Gewerke arbeiten an derselben Absturzkante und behindern sich beim Material.',
        'Türöffnungen bis zum Türeinbau mit tragfähiger, gegen Verschieben gesicherter Absperrung schließen. Reihenfolge Schachtwand/Türmontage als harte Abhängigkeit im Terminplan; Anschlagpunkte für PSA gegen Absturz im Schacht ausweisen.',
        ['ASR A2.1', 'TRBS 2121 Teil 1']),

    p('GK-058', 'aufzugsbau', 'reinigung', 'mittel', 'baustellenweit',
        'Der Aufzug wird vor Abnahme als Transportmittel für Reinigungsgerät und Abfall benutzt. Fahrkorb, Türverriegelung und Bündigkeit sind noch nicht geprüft.',
        'Nutzung erst nach Abnahme und schriftlicher Freigabe zulassen und die Bedienung bis dahin verriegeln. Transportweg für die Reinigung (Bauaufzug, Treppe) ausdrücklich benennen, sonst wird der Aufzug trotzdem benutzt.',
        ['BetrSichV', 'TRBS 3121']),

    p('GK-059', 'trockenbau', 'maler', 'mittel', 'gleicher_bereich',
        'Beide arbeiten im selben Raum von Fahrgerüsten aus, die mit Person und Material verfahren werden. Schleifstaub gelangt in frische Beschichtungen und in den Atembereich des jeweils anderen.',
        'Raumbelegung zeitlich staffeln statt parallel zu belegen. Fahrgerüste nur unbelastet verfahren und Rollen feststellen; Schleifarbeiten mit Absaugung und nicht zeitgleich mit Beschichtungsarbeiten im selben Raum.',
        ['TRBS 2121 Teil 1', 'TRGS 559']),

    p('GK-060', 'trockenbau', 'fliesenleger', 'mittel', 'gleicher_bereich',
        'Fliesenschneiden und Trockenbauschleifen im selben Raum ergeben eine gemeinsame Staubbelastung; das Schneidwasser durchfeuchtet Gipsbauteile, die dann Tragfähigkeit verlieren.',
        'Staub- und nassarbeitsintensive Vorgänge zeitlich trennen und Schneidplätze aus dem Raum verlegen. Absaugung an den Geräten und Wasserführung so festlegen, dass Gipsbauteile nicht durchfeuchtet werden.',
        ['TRGS 559', 'GefStoffV']),

    p('GK-061', 'maler', 'reinigung', 'mittel', 'gleicher_bereich',
        'Gereinigt wird in frisch beschichteten, noch nicht abgelüfteten Räumen. Lösemitteldämpfe sammeln sich, und frische Beschichtungen sind rutschig.',
        'Ablüftzeiten je Raum als Sperrfrist kennzeichnen und im Belegungsplan führen. Lüftung vor Reinigungsbeginn sicherstellen; Freigabe des Raumes durch den Maler zur Voraussetzung für die Reinigung machen.',
        ['GefStoffV', 'ASR A3.6']),

    // ─── Verkehrsflächen und Außenanlagen ─────────────────────────────────
    p('GK-062', 'galabau', 'strassenbau', 'mittel', 'benachbart',
        'Beide Gewerke nutzen dieselbe Verkehrsfläche als Arbeitsplatz und als Fahrweg. Kipper und Radlader fahren rückwärts, während Beschäftigte zu Fuß im Bereich arbeiten.',
        'Fahr- und Gehwege trennen, beschildern und bei jedem Bauphasenwechsel fortschreiben. Rückwärtsfahren nur mit Einweiser oder Rückfahrassistenz; einen gemeinsamen Verkehrswegeplan führen und einer Person zuweisen.',
        ['DGUV Vorschrift 38 § 4', 'ASR A1.8']),

    p('GK-063', 'strassenbau', 'strassenbau', 'mittel', 'benachbart',
        'Zwei Straßenbaulose sichern denselben Verkehrszug unterschiedlich ab. Widersprüchliche Beschilderung führt Verkehrsteilnehmer in den Arbeitsbereich des jeweils anderen Loses.',
        'Verkehrssicherung für den gesamten Verkehrszug einem Unternehmer zuweisen und Änderungen nur über ihn zulassen. Übergangsstellen zwischen den Losen in der verkehrsrechtlichen Anordnung ausdrücklich regeln.',
        ['RSA 21', '§ 45 StVO', 'DGUV Vorschrift 1 § 6']),

    // ─── Spezialtiefbau (Fassung 2) ───────────────────────────────────────
    p('GK-064', 'spezialtiefbau', 'erdbau', 'hoch', 'gleicher_bereich',
        'Der Aushub eilt dem Verbau voraus oder Aussteifungen werden für die Baggerzufahrt entfernt. Die Wand verformt sich, Boden bricht nach — verschüttet werden die, die in der Sohle arbeiten, nicht die auf dem Gerät.',
        'Aushub- und Aussteifungsfolge als verbindliche Reihenfolge festschreiben und Freigabepunkte je Aushubebene vereinbaren; Ausbau von Steifen nur nach schriftlicher Freigabe des Verbaufachbetriebs. Verformungsmessung mit Grenzwert und Stopp-Regel, die jeder auslösen darf.',
        ['DIN 4124', 'Anhang II Nr. 1 BaustellV', 'DGUV Vorschrift 38']),

    p('GK-065', 'spezialtiefbau', 'strassenbau', 'hoch', 'benachbart',
        'Bohr- und Rammgeräte mit hohem Mäkler arbeiten unmittelbar neben dem geführten Verkehr. Die Aufstandsfläche liegt am Fahrbahnrand, der Mäkler ragt in den Luftraum, und Rammerschütterungen wirken auf die Fahrbahn.',
        'Aufstellflächen mit Nachweis der Tragfähigkeit festlegen und den Schwenk- und Kippbereich des Geräts als Sperrfläche in die verkehrsrechtliche Anordnung aufnehmen. Geräteumsetzung nur in abgestimmten Sperrzeiten; Erschütterungsmessung, wenn der Verkehrsweg selbst betroffen ist.',
        ['RSA 21', 'ASR A5.2', '§ 45 StVO', 'DGUV Vorschrift 38']),

    p('GK-066', 'spezialtiefbau', 'elektro', 'hoch', 'gleicher_bereich',
        'Bohrpfahl, Spundwand oder Anker treffen erdverlegte Kabel, deren Tiefenlage im Bestandsplan ungenau ist; der Mäkler nähert sich zugleich einer Freileitung. Beide Ereignisse treffen Gerätefahrer und Elektrofachkraft gemeinsam.',
        'Trassen vor dem ersten Ansatzpunkt orten, einmessen und jeden Bohransatz einzeln freigeben lassen — eine Flächenfreigabe genügt nicht. Freileitungen abschalten oder abdecken lassen, sonst Schutzabstand absperren und Mäklerhöhe begrenzen.',
        ['DGUV Vorschrift 3', 'Anhang II Nr. 4 BaustellV', 'DGUV Vorschrift 38']),

    p('GK-067', 'spezialtiefbau', 'kran', 'hoch', 'gleicher_bereich',
        'Mäkler des Bohrgeräts und Kranausleger bewegen sich im selben Luftraum, und beide Geräte stehen an der Baugrubenkante. Eine Kollision setzt eine ungeführte Last oder ein kippendes Gerät frei.',
        'Luftraum staffeln und die Bewegungsbereiche beider Geräte gegeneinander abgrenzen; wo sie sich schneiden, gilt eine schriftliche Vorrangregelung mit gemeinsamer Funkfrequenz. Aufstell- und Fahrflächen beider Geräte mit Abstand zur Kante nachweisen.',
        ['DGUV Vorschrift 52', 'DIN 4124', 'DGUV Vorschrift 38']),

    p('GK-068', 'spezialtiefbau', 'rohbau', 'hoch', 'benachbart',
        'Rammen, Rütteln und Unterfangen erzeugen Setzungen und Erschütterungen, die auf Nachbarbebauung und auf frisch erstellte Bauteile wirken. Der Rohbau arbeitet in oder an einem Bauwerk, dessen Gründung gerade verändert wird.',
        'Beweissicherung und Erschütterungsmessung an Nachbarbebauung und Bestand vor Beginn veranlassen, mit Grenzwerten und benannter Stelle für den Abbruch der Arbeiten. Unterfangungsabschnitte und Rohbauarbeiten im selben Bauteil zeitlich trennen.',
        ['DIN 4123', 'DGUV Vorschrift 38', '§ 3 Abs. 3 BaustellV']),

    // ─── Dachklempner (Fassung 2) ─────────────────────────────────────────
    p('GK-069', 'dachklempner', 'zimmerer_dachdecker', 'hoch', 'gleicher_bereich',
        'Beide arbeiten auf derselben Dachfläche: Abdichtung und Blecharbeiten greifen ineinander, dabei wird gelötet und geflämmt — unmittelbar auf Holzkonstruktion und brennbarer Dämmung, die das andere Gewerk gerade eingebaut hat.',
        'Arbeitsabschnitte auf der Dachfläche gegeneinander abgrenzen und Heißarbeiten nur mit Erlaubnisschein, Brandwache und festgelegter Nachkontrolle zulassen. Die Absturzsicherung der Dachfläche einem Gewerk als Vorhalteleistung zuweisen, damit sie beim Gewerkewechsel nicht verschwindet.',
        ['TRGS 528', 'ASR A2.2', 'ASR A2.1', 'BetrSichV']),

    p('GK-070', 'dachklempner', 'geruestbau', 'hoch', 'gleicher_bereich',
        'Attika-, Rinnen- und Anschlussbleche liegen genau dort, wo Seitenschutz und Dachfanglage stehen. Für den Blechanschluss wird der Seitenschutz geöffnet — an der höchsten Stelle des Bauwerks.',
        'Gerüst so planen, dass Attika und Dachrand ohne Ausbau des Seitenschutzes erreichbar sind; wo das nicht geht, abschnittsweises Öffnen mit PSA gegen Absturz an ausgewiesenen Anschlagpunkten und sofortigem Wiederherstellen. Standzeit der Dachfanglage bis zum Abschluss der Blecharbeiten sichern.',
        ['TRBS 2121 Teil 1', 'ASR A2.1']),

    p('GK-071', 'dachklempner', 'elektro', 'hoch', 'gleicher_bereich',
        'Fangeinrichtung, Ableitungen und der Blitzschutzanschluss an Attika und Rinne werden hergestellt, während die Elektrofachkraft an derselben Anlage misst oder anschließt; auf demselben Dach steht eine Photovoltaikanlage, die auch bei getrenntem Wechselrichter gleichstromseitig unter Spannung steht.',
        'Arbeiten an Blitzschutz und Photovoltaik einer benannten Person unterstellen, die den Zustand der Anlage kennt und freigibt; Anschlussarbeiten und Messungen zeitlich trennen. PV-Bereiche als spannungsführend kennzeichnen, solange sie nicht nachweislich freigeschaltet und geerdet sind.',
        ['DGUV Vorschrift 3', 'DIN EN 62305', '§ 3 Abs. 3 BaustellV']),

    p('GK-072', 'dachklempner', 'fassade', 'hoch', 'uebereinander',
        'Anschluss- und Attikableche werden über der laufenden Fassadenmontage eingebaut. Blechzuschnitte, Werkzeug und Befestigungsmittel fallen auf die Monteure darunter, die beide dasselbe Gerüst nutzen.',
        'Arbeiten in derselben Achse zeitlich oder seitlich trennen; wo das nicht geht, Schutzdach oder Fanglage zwischen den Ebenen. Materialablage auf dem Dachrand gegen Abrutschen sichern und die Gerüstbelegung beider Gewerke im Belegungsplan regeln.',
        ['ASR A2.1', 'TRBS 2121 Teil 1']),

    p('GK-073', 'dachklempner', 'stuckateur', 'hoch', 'benachbart',
        'Am Attika- und Dachrandanschluss wird gelötet oder geflämmt, während unmittelbar darunter das Wärmedämmverbundsystem sitzt. Die Dämmung ist brennbar, der Brand läuft im Fassadenhohlraum nach oben und ist von außen nicht erreichbar — das ist der klassische Großschadenfall.',
        'Heißarbeiten am Dachrand erst nach Fertigstellung und Verschluss der darunterliegenden Dämmfläche zulassen, sonst offene Dämmränder brandschutztechnisch abdecken. Erlaubnisschein, Brandwache während und nach den Arbeiten sowie Nachkontrolle des Fassadenhohlraums verbindlich festlegen; wo möglich Falz- statt Lötverbindung vorgeben.',
        ['TRGS 528', 'ASR A2.2', 'BetrSichV']),

    // ─── Stuckateur / WDVS (Fassung 2) ────────────────────────────────────
    p('GK-074', 'stuckateur', 'geruestbau', 'hoch', 'gleicher_bereich',
        'Das WDVS wird auf der Wand aufgebaut, in der die Gerüstanker sitzen. Anker werden für die Dämmung gezogen, umgesetzt oder überdämmt, und der Wandabstand des Gerüsts passt nach dem Dämmauftrag nicht mehr.',
        'Ankerplan und Umsetzfolge vor Beginn gemeinsam festlegen: Ziehen und Umsetzen ausschließlich durch den Gerüstersteller, nach jedem Schritt Prüfung und erneute Freigabe. Wandabstand von vornherein auf die Dämmstärke auslegen, damit später keine Beläge entfernt werden.',
        ['TRBS 2121 Teil 1', 'BetrSichV', 'ASR A2.1']),

    p('GK-075', 'stuckateur', 'shk', 'hoch', 'benachbart',
        'Löt-, Schweiß- und Trennarbeiten an Leitungen und Halterungen finden an einer Fassade statt, an der bereits brennbare Dämmung steht oder offene Dämmränder liegen. Der Brand entwickelt sich im Hohlraum hinter der Bekleidung.',
        'Heißarbeiten an der gedämmten Fassade nur mit Erlaubnisschein, nach Abdeckung offener Dämmränder und mit Brandwache über die Arbeitszeit hinaus. Durchdringungen der Dämmebene vorher planen, damit nachträglich nicht mit Zündquellen gearbeitet werden muss.',
        ['TRGS 528', 'ASR A2.2', 'BetrSichV']),

    p('GK-076', 'stuckateur', 'elektro', 'hoch', 'gleicher_bereich',
        'Maschinenputz und Nassputz werden auf Wände aufgetragen, in denen die Installation offen liegt; Wasser und Putzmörtel erreichen Dosen, Leitungsenden und den Verteiler, während andernorts zugeschaltet wird.',
        'Stromkreise im Putzbereich vor Beginn freischalten und schriftlich freimelden, Dosen und Leitungsenden verschließen. Baustromversorgung der Putzmaschine über eine eigene, fehlerstromgeschützte Speisestelle außerhalb des Nassbereichs führen.',
        ['DGUV Vorschrift 3', 'DGUV Information 203-006']),

    p('GK-077', 'stuckateur', 'fassade', 'mittel', 'gleicher_bereich',
        'Fensteranschluss und Dämmung greifen ineinander: Wird gedämmt, bevor die Fenster gesetzt und die Anschlussfolien geklebt sind, muss die Dämmung wieder geöffnet werden — beide Gewerke arbeiten dann gleichzeitig am selben Gerüstfeld.',
        'Reihenfolge Fenstereinbau, Anschluss, Dämmung als harte Abhängigkeit im Terminplan hinterlegen und Gerüstfelder abschnittsweise übergeben. Übergaben zwischen den Gewerken schriftlich festhalten, damit niemand hinter dem anderen herarbeitet.',
        ['§ 3 Abs. 3 BaustellV']),

    p('GK-078', 'stuckateur', 'trockenbau', 'mittel', 'gleicher_bereich',
        'Innenputz bringt große Mengen Wasser in Räume, in denen Gipsplatten stehen oder gerade montiert werden; Trocknungsgeräte laufen, und beide Gewerke arbeiten von Fahrgerüsten im selben Raum.',
        'Nassputz und Beplankung im selben Raum zeitlich trennen und Trocknungszeiten als eigenen Vorgang führen. Standorte und Stromversorgung der Trocknungsgeräte abstimmen; Verkehrswege im Raum freihalten, solange Gerüste verfahren werden.',
        ['ASR A1.8', 'TRBS 2121 Teil 1']),

    // ─── Sprinkler / Brandschutztechnik (Fassung 2) ───────────────────────
    p('GK-079', 'sprinkler', 'elektro', 'hoch', 'gleicher_bereich',
        'Die Löschanlage wird befüllt und mit Druck beaufschlagt, während unter derselben Decke die Elektroinstallation offen liegt oder bereits unter Spannung steht. Eine undichte Verbindung entleert eine große Wassermenge über Verteiler, Trassen und Leuchten — und trifft die, die dort arbeiten.',
        'Druckprobe und Befüllung als eigenen Vorgang terminieren, ankündigen und den betroffenen Bereich vorher räumen; Stromkreise unter der Leitungsführung für die Dauer freischalten und freimelden. Auffang- und Ablaufweg für Wasser festlegen und eine Person benennen, die die Anlage im Schadensfall absperrt.',
        ['DGUV Vorschrift 3', 'DGUV Information 203-006', 'BetrSichV']),

    p('GK-080', 'sprinkler', 'trockenbau', 'hoch', 'gleicher_bereich',
        'Löschleitungen und Brandschottungen liegen im Deckenhohlraum, der gerade geschlossen wird. Die Decke wird vor der Druckprobe beplankt, Schotts werden nachträglich durchbohrt, und im geschlossenen Hohlraum wird mit Zündquellen gearbeitet.',
        'Druckprobe mit Protokoll und Abnahme der Abschottungen als Freigabevoraussetzung für das Schließen der Decke festlegen und als harte Abhängigkeit im Terminplan führen. Nachträgliche Durchdringungen von Schotts nur mit Freigabe der ausführenden Fachfirma; Heißarbeiten im Hohlraum nur mit Erlaubnisschein und Brandwache.',
        ['ASR A2.2', 'TRGS 528', '§ 3 Abs. 3 BaustellV']),

    p('GK-081', 'sprinkler', 'shk', 'mittel', 'gleicher_bereich',
        'Löschanlage und Haustechnik teilen sich Trasse und Deckenhohlraum. Beide arbeiten von Hubarbeitsbühnen unter derselben Decke, und Absperr- und Entleerungszustände sind zwischen den Anlagen nicht abgestimmt.',
        'Trassenbelegung und Montagereihenfolge vorab festlegen und die Deckenzone abschnittsweise übergeben. Absperr-, Füll- und Entleerungszustände beider Gewerke gemeinsam dokumentieren; Prüfungen ankündigen und den Bereich darunter räumen.',
        ['§ 3 Abs. 3 BaustellV', 'BetrSichV']),

    // ─── Tischler (Fassung 2) ─────────────────────────────────────────────
    p('GK-082', 'tischler', 'estrich', 'mittel', 'gleicher_bereich',
        'Einbauten und Türzargen werden auf noch nicht belegreifem Estrich montiert und verankert. Die Bauteile arbeiten mit der Restfeuchte nach, Befestigungen lösen sich; zugleich wird auf frischem Estrich zwischen laufenden Trocknungsgeräten gearbeitet.',
        'Belegreife messen, dokumentieren und erst dann für Montagearbeiten freigeben; Sperrfristen je Feld kennzeichnen. Standorte der Trocknungsgeräte, Transportwege und Stromversorgung abstimmen, damit Kabel und Schläuche nicht zur Stolperstelle im Montagebereich werden.',
        ['ASR A1.8', '§ 3 Abs. 3 BaustellV']),

    p('GK-083', 'tischler', 'maler', 'mittel', 'gleicher_bereich',
        'Zuschnitt und Schliff erzeugen Holzstaub, während im selben Raum lösemittelhaltig beschichtet wird. Holzstaub ist krebserzeugend, und Elektrowerkzeug ist eine Zündquelle in einer Atmosphäre aus Lösemitteldämpfen.',
        'Zuschnitt- und Schleifarbeiten aus dem Beschichtungsraum verlegen oder zeitlich trennen und ausschließlich mit Absaugung ausführen. Lüftung vor Beginn der Beschichtungsarbeiten sicherstellen und für diese Zeit Zündquellenfreiheit im Raum vereinbaren.',
        ['TRGS 553', 'GefStoffV', 'ASR A3.6']),

    p('GK-084', 'tischler', 'trockenbau', 'mittel', 'gleicher_bereich',
        'Türzargen, Einbauten und Trockenbauwände hängen voneinander ab: Wird beplankt, bevor die Befestigungsuntergründe stehen, muss die Wand später geöffnet werden — und beide Gewerke schleifen und schneiden dann gleichzeitig im selben Raum.',
        'Lage der Befestigungsuntergründe und Zargen vor der Beplankung abstimmen und dokumentieren; Reihenfolge als harte Abhängigkeit im Terminplan. Staubintensive Arbeiten beider Gewerke zeitlich staffeln und mit Absaugung ausführen.',
        ['TRGS 559', '§ 3 Abs. 3 BaustellV']),
];

/**
 * Schluessel eines Gewerkepaares, unabhaengig von der Reihenfolge.
 *
 * ─── Warum die Paarung ungerichtet ist ────────────────────────────────────
 * § 3 BaustellV verpflichtet den Koordinator auf die GEGENSEITIGE
 * Gefaehrdung. Eine gerichtete Ablage ("Kran gefaehrdet Rohbau") wuerde die
 * Rueckrichtung entweder verdoppeln oder verschweigen — und verschwiegen
 * hiesse: der Rohbauunternehmer bekaeme den Hinweis nicht, weil er in seiner
 * Zeile nicht vorkommt. Deshalb gilt A/B = B/A, und beide Arbeitgeber sind
 * Adressaten derselben Massnahme.
 *
 * Die Reihenfolge im Katalog ist reine Lesbarkeit: vorn steht meist das
 * Gewerk, von dem die Einwirkung ausgeht. Wer daraus eine Richtung ableitet,
 * liest mehr hinein, als der Eintrag hergibt — der Text nennt ausdruecklich,
 * wer wen trifft. Beim Uebertragen in `MutualHazard` (tradeSource /
 * tradeAffected) ist `a` deshalb nur die Nennreihenfolge, keine Zuweisung
 * von Verantwortung.
 */
export const paarSchluessel = (a: GewerkId, b: GewerkId): string =>
    [a, b].sort().join('~');

const PAARUNG_NACH_SCHLUESSEL = new Map<string, GewerkePaarung>(
    PAARUNGEN.map(x => [paarSchluessel(x.a, x.b), x]),
);

/** Genau eine Paarung je Gewerkepaar — die Eindeutigkeit ist Invariante des
 *  Katalogs (siehe Test). Zwei Eintraege zum selben Paar wuerden bedeuten,
 *  dass eine der beiden Massnahmen im Plan fehlt, sobald jemand nur den
 *  ersten Treffer verwendet. */
export const findePaarung = (a: GewerkId, b: GewerkId): GewerkePaarung | undefined =>
    PAARUNG_NACH_SCHLUESSEL.get(paarSchluessel(a, b));

// ═══════════════════════════════════════════════════════════════════════════
// REGELN OHNE PAARUNG
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Gefaehrdungen, die ein Gewerk gegen JEDES gleichzeitig taetige andere
 * Gewerk erzeugt.
 *
 * Diese Faelle als Paarungen zu fuehren hiesse, sie achtzehnmal
 * hinzuschreiben — und beim naechsten neuen Gewerk achtzehn Stellen zu
 * pflegen. Fachlich sind sie ohnehin etwas anderes: der Gefahrbereich einer
 * schwebenden Last kennt das Gewerk nicht, das er trifft.
 */
export interface GewerkRegel {
    code: string;
    gewerk: GewerkId;
    schwere: Schwere;
    raumbezug: RaumBezug;
    gefaehrdung: string;
    massnahme: string;
    vorschriften: string[];
}

export const GEWERK_REGELN: GewerkRegel[] = [
    {
        code: 'GR-01',
        gewerk: 'kran',
        schwere: 'hoch',
        raumbezug: 'uebereinander',
        gefaehrdung: 'Der Gefahrbereich unter der schwebenden Last trifft jedes Gewerk, das sich dort aufhält — auch für wenige Sekunden auf dem Weg zum Container.',
        massnahme: 'Lastwege und Absetzplätze für alle Gewerke sichtbar kennzeichnen und in der Einweisung in den SiGe-Plan behandeln. Verkehrswege so führen, dass sie Lastwege nicht kreuzen.',
        vorschriften: ['ASR A2.1', 'DGUV Vorschrift 52', 'ASR A1.8'],
    },
    {
        code: 'GR-02',
        gewerk: 'geruestbau',
        schwere: 'hoch',
        raumbezug: 'gleicher_bereich',
        gefaehrdung: 'Auf-, Um- und Abbau erzeugen einen Gefahrbereich für alle Nutzer des Gerüsts; das Veränderungsverbot gilt gegenüber jedem Gewerk, nicht nur gegenüber dem Auftraggeber des Gerüsts.',
        massnahme: 'Freigabekennzeichnung mit Lastklasse, Datum und Ersteller am Zugang; gesperrte Abschnitte körperlich absperren. Änderungswünsche jedes Gewerks laufen über eine benannte Stelle beim Gerüstersteller.',
        vorschriften: ['TRBS 2121 Teil 1', 'BetrSichV', 'ASR A1.3'],
    },
    {
        code: 'GR-03',
        gewerk: 'abbruch',
        schwere: 'hoch',
        raumbezug: 'benachbart',
        gefaehrdung: 'Einsturz, herabfallende Bauteile, Staub und Erschütterung wirken über den eigenen Arbeitsbereich hinaus auf jedes gleichzeitig anwesende Gewerk.',
        massnahme: 'Gefahrbereich aus der Abbruchanweisung ableiten, absperren und im SiGe-Plan verorten; Zutritt nur über eine benannte Stelle. Abbruchphasen und Fremdgewerke terminlich entzerren statt räumlich zu verschachteln.',
        vorschriften: ['DGUV Vorschrift 38', 'ASR A2.1'],
    },
    {
        code: 'GR-04',
        gewerk: 'schadstoff',
        schwere: 'hoch',
        raumbezug: 'benachbart',
        gefaehrdung: 'Der Sanierungsbereich ist für jedes andere Gewerk gesperrt. Jeder Zutritt und jede Öffnung gefährdet den Zutretenden und hebt den Schutz für die bereits sanierten Bereiche auf.',
        massnahme: 'Bereichsgrenzen, Schleusen und Freimessung im SiGe-Plan verorten und in jeder Einweisung behandeln. Angrenzende Arbeiten erst nach schriftlicher Freimessung freigeben; Stopp-Regel bei unerwarteten Funden für alle Gewerke.',
        vorschriften: ['TRGS 519', 'GefStoffV § 15', 'Anhang II Nr. 2 BaustellV'],
    },
    {
        code: 'GR-05',
        gewerk: 'elektro',
        schwere: 'hoch',
        raumbezug: 'baustellenweit',
        gefaehrdung: 'Freischaltung, Zuschaltung und Inbetriebnahme wirken auf die ganze Baustelle. Wer ohne Kenntnis des Schaltzustands an einer Anlage arbeitet, verlässt sich auf eine Annahme.',
        massnahme: 'Einen Schaltberechtigten benennen, Schaltzustände zentral dokumentieren und Freimeldungen ausschließlich schriftlich erteilen. Baustromverteiler, Prüffristen und Zuständigkeit für die Prüfung baustellenweit regeln.',
        vorschriften: ['DGUV Vorschrift 3', 'DGUV Information 203-006'],
    },
    {
        code: 'GR-06',
        gewerk: 'erdbau',
        schwere: 'hoch',
        raumbezug: 'gleicher_bereich',
        gefaehrdung: 'Offene Baugruben und Gräben sowie der Erdbaugeräteverkehr gefährden jedes Gewerk, das den Bereich quert oder an der Kante arbeitet oder lagert.',
        massnahme: 'Kanten sichern und kennzeichnen, Übergänge an festgelegten Stellen herstellen, Lager- und Fahrverbotszonen ausweisen. Fahrwege der Erdbaugeräte von den Fußwegen der übrigen Gewerke trennen.',
        vorschriften: ['DIN 4124', 'ASR A2.1', 'DGUV Vorschrift 38 § 4'],
    },
    {
        code: 'GR-07',
        gewerk: 'strassenbau',
        schwere: 'hoch',
        raumbezug: 'benachbart',
        gefaehrdung: 'Die Verkehrsführung wirkt auf jedes Gewerk, das die Verkehrsfläche betritt, quert oder beliefert — einschließlich Anlieferungen fremder Gewerke, die im Sicherungskonzept nicht vorkommen.',
        massnahme: 'Verkehrssicherung, Zufahrten und Anlieferungspunkte für alle Gewerke gemeinsam festlegen und in der verkehrsrechtlichen Anordnung abbilden. Warnkleidungspflicht für den gesamten Bereich, nicht nur für die Straßenbaukolonne.',
        vorschriften: ['RSA 21', 'ASR A5.2', '§ 45 StVO'],
    },
    {
        code: 'GR-08',
        gewerk: 'spezialtiefbau',
        schwere: 'hoch',
        raumbezug: 'benachbart',
        gefaehrdung: 'Setzungen, Erschütterungen und die Verformung des Verbaus wirken über den eigenen Arbeitsbereich hinaus — auf Nachbarbebauung, auf Leitungen und auf jedes Gewerk, das im Einflussbereich der Baugrube arbeitet oder lagert.',
        massnahme: 'Einflussbereich aus der Verbauplanung ableiten, im SiGe-Plan verorten und Lager- und Fahrverbotszonen daraus bilden. Beweissicherung, Verformungs- und Erschütterungsmessung mit Grenzwerten und einer Stopp-Regel, die jedem Gewerk offensteht.',
        vorschriften: ['DIN 4124', 'DIN 4123', 'DGUV Vorschrift 38'],
    },
    {
        code: 'GR-09',
        gewerk: 'dachklempner',
        schwere: 'hoch',
        raumbezug: 'uebereinander',
        gefaehrdung: 'Blecharbeiten finden an der höchsten und ungeschütztesten Stelle des Bauwerks statt. Zuschnitte, Werkzeug und Befestigungsmittel treffen jedes Gewerk darunter, und die Heißarbeiten am Dachrand wirken als Brandlast auf alles, was darunter eingebaut ist.',
        massnahme: 'Für die Dauer der Dachrandarbeiten den Bereich darunter sperren oder mit Schutzdach sichern und die Sperrung in der Einweisung in den SiGe-Plan behandeln. Heißarbeiten nur mit Erlaubnisschein, Brandwache und Nachkontrolle — auch der darunterliegenden Ebenen.',
        vorschriften: ['ASR A2.1', 'ASR A2.2', 'TRGS 528'],
    },
];

/**
 * Ab wie vielen verschiedenen Gewerken im selben Zeitfenster und Bereich eine
 * Koordinierungsbesprechung faellig wird.
 *
 * Der Wert ist eine Erfahrungsgroesse, keine Rechtsnorm — die BaustellV nennt
 * keine Zahl. Er ist bewusst niedrig: bei vier gleichzeitigen Gewerken gibt
 * es schon sechs Gewerkepaare, und die haelt niemand mehr nebenbei im Kopf.
 */
export const SCHWELLE_KOORDINIERUNGSBESPRECHUNG = 3;

/**
 * Kuerzeste Ueberschneidung in Tagen, die als Gleichzeitigkeit zaehlt.
 *
 * `zeitraum_von`/`zeitraum_bis` sind Kalendertage und beidseitig
 * einschliessend: endet ein Gewerk am 12. und beginnt das naechste am 12.,
 * ist das ein gemeinsamer Tag — und genau der Uebergabetag ist der Tag, an
 * dem sich Kolonnen begegnen. Deshalb 1 und nicht 2.
 */
export const MINDEST_UEBERLAPPUNG_TAGE = 1;

/**
 * Regeln, die allein aus der Gleichzeitigkeit folgen — ohne Ansehen der
 * beteiligten Gewerke.
 */
export interface AllgemeineRegel {
    code: string;
    bedingung:
        | 'mehr_als_schwelle'      // mehr als SCHWELLE_KOORDINIERUNGSBESPRECHUNG Gewerke gleichzeitig
        | 'mehrere_arbeitgeber'    // ueberhaupt mehr als ein Arbeitgeber gleichzeitig
        | 'gewerk_unbekannt'       // mindestens ein Gewerk nicht zugeordnet
        | 'zeitraum_fehlt'         // Arbeitsschritt ohne zeitraum_von/bis
        | 'anhang_zwei_parallel';  // Arbeit nach Anhang II BaustellV parallel zu anderen
    schwere: Schwere;
    hinweis: string;
    massnahme: string;
    vorschriften: string[];
}

export const ALLGEMEINE_REGELN: AllgemeineRegel[] = [
    {
        code: 'AR-01',
        bedingung: 'mehr_als_schwelle',
        schwere: 'mittel',
        hinweis: `Mehr als ${SCHWELLE_KOORDINIERUNGSBESPRECHUNG} Gewerke arbeiten im selben Zeitfenster und Bereich.`,
        massnahme: 'Koordinierungsbesprechung mit allen betroffenen Unternehmern ansetzen, Ergebnis protokollieren und den SiGe-Plan fortschreiben. Verkehrswege, Lagerflächen und die Nutzung gemeinsamer Einrichtungen für dieses Zeitfenster neu festlegen.',
        vorschriften: ['§ 3 Abs. 3 BaustellV', 'RAB 31', 'DGUV Vorschrift 1 § 6'],
    },
    {
        code: 'AR-02',
        bedingung: 'mehrere_arbeitgeber',
        schwere: 'mittel',
        hinweis: 'Beschäftigte mehrerer Arbeitgeber sind gleichzeitig tätig.',
        massnahme: 'Gemeinsam genutzte Einrichtungen benennen (Gerüst, Kran, Bauaufzug, Verkehrswege, Absturzsicherungen, Baustrom, Erste Hilfe) und für jede einen Verantwortlichen für Stellung, Prüfung, Erhalt und Rückbau festlegen.',
        vorschriften: ['§ 8 ArbSchG', 'DGUV Vorschrift 1 § 6', '§ 3 Abs. 3 BaustellV'],
    },
    {
        code: 'AR-03',
        bedingung: 'gewerk_unbekannt',
        schwere: 'mittel',
        hinweis: 'Mindestens ein beteiligtes Gewerk konnte dem Katalog nicht zugeordnet werden.',
        massnahme: 'Das Gewerk am Arbeitsschritt eindeutig benennen und die Überschneidung anschließend erneut bewerten. Ein nicht zugeordnetes Gewerk ist KEIN Nachweis der Gefährdungsfreiheit — bis zur Klärung gilt die Überschneidung als ungeprüft.',
        vorschriften: ['§ 3 Abs. 2 BaustellV'],
    },
    {
        code: 'AR-04',
        bedingung: 'zeitraum_fehlt',
        schwere: 'gering',
        hinweis: 'Der Arbeitsschritt hat keinen oder keinen vollständigen Zeitraum und nimmt an der Überschneidungsprüfung nicht teil.',
        massnahme: 'Zeitraum am Arbeitsschritt ergänzen oder den Schritt ausdrücklich als zeitlich nicht gebunden kennzeichnen. Ohne Zeitraum entsteht der Eindruck, es gäbe keine Überschneidung.',
        vorschriften: ['§ 2 Abs. 1 BaustellV'],
    },
    {
        code: 'AR-05',
        bedingung: 'anhang_zwei_parallel',
        schwere: 'hoch',
        hinweis: 'Eine Arbeit mit besonderen Gefahren nach Anhang II BaustellV läuft parallel zu Arbeiten anderer Gewerke.',
        massnahme: 'Zuerst zeitliche Entzerrung prüfen — die Rangfolge des § 4 ArbSchG verlangt, die Gefahr an der Quelle zu vermeiden, bevor Schutzmaßnahmen ergriffen werden. Bleibt die Parallelität, gehört sie mit besonderer Maßnahme in den SiGe-Plan.',
        vorschriften: ['Anhang II BaustellV', '§ 4 Nr. 1 ArbSchG', 'RAB 33'],
    },
];
